import { IconChevronDown, IconSearch } from '@tabler/icons-react';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Drawer, FormControlLabel,
  IconButton, InputAdornment, MenuItem, Radio, Switch, TextField, Typography,
} from '@mui/material';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { fetchAvailableDateBoundsAndPlanYear, fetchGlobalFilterOptions, fetchCohortOptions, resetGlobalFilterOptions } from 'features/frontoffice/actions/actions';
import { defaultAppliedFilterValues, PRIOR_COMPARISON_DISABLED_DATE } from 'features/globalFilters/globalFiltersDefaults';
import { normalizeGlobalFilterOptionLabels } from 'features/globalFilters/globalFilterOptionUtils';
import { fetchSavedFilter } from 'features/globalFilters/globalFiltersActions';
import { getUniquePersonIdValues, normalizePersonIdValues } from 'features/globalFilters/personIdUtils';
import { fetchValidPersonIds } from 'features/globalFilters/personIdValidationService';
import { DATE_OPTION_ERROR, DATE_ORDER_ERROR, FORMAT_ERROR, PERSON_ID_CHARACTER_LIMIT_ERROR, PERSON_ID_FORMAT_ERROR } from 'features/landing/navigation/top/globalFilterErrorMessages';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { colors, drawerDividerColor, drawerDividerWidth, drawerShadow, getDropdownFieldSx,
  getDropdownPaperSx, getTimeWindowSelectSx, getTimeWindowMenuProps, helperTextSx, dateInputLabelSx, errorTextSx,
  warningTextSx, optionLabelSx, disabledOptionLabelSx, getDropdownOptionSx, getTimeWindowMenuItemSx, checkboxSx, radioSx, selectionPanelSx, getDateRangePanelSx,
  getErrorFieldSx, getSelectionPanelSx, loadingPanelSx, buttonContainedSx, dateBasisGuidanceTextSx, radioOptionRowSx,
  getDatePartSelectSx, getDateDropdownOptionSx, getDateDropdownPaperSx, lockedDateFieldSx, lockedDateTextSx, lockedDateControlSx, buttonOutlinedSx, getResetButtonSx,
  priorComparisonHelperTextSx, priorComparisonSwitchSx, checklistSearchFieldSx, checklistEmptyStateSx,
} from './globalFiltersStyles';
import StyledTooltip from 'common/StyledTooltip';
import FilterSection from './components/FilterSection';
import PopulationFilters from './components/PopulationFilters';
import { POPULATION_MODE } from 'common/Constant';
import { calculateTimeWindowDates } from 'utils/timeWindowUtil';

const LOCKED_DATE_FIELD_MESSAGE = 'Switch to Custom to edit dates.';

const timeWindowOptions = [
  { label: 'Rolling 12 Months', value: 'R12' },
  { label: 'Plan YTD (Plan Year-to-Date) vs Prior Plan YTD', value: 'PYTD_VS_PPYTD' },
  { label: 'Plan YTD vs Prior Plan Year', value: 'PYTD_VS_PPY' },
  { label: 'Custom Range', value: 'CUSTOM' },
];

const divisionOptions = [{ label: 'All Divisions', checked: true }];

const planOptions = [{ label: 'All Plans', checked: true }];

const stripAllLabel = (labels, allLabel) => labels.filter((label) => label !== allLabel);

const normalizeGlobalFilterOptions = (filterOptions) => ({
  division: normalizeGlobalFilterOptionLabels(filterOptions?.division),
  plan: normalizeGlobalFilterOptionLabels(filterOptions?.plan),
});

const emptyGlobalFilterOptions = Object.freeze({ division: [], plan: [] });
const emptyDateRangeValues = Object.freeze({
  currentPeriodFromValue: '',
  currentPeriodToValue: '',
  priorPeriodFromValue: '',
  priorPeriodToValue: '',
});

const summarizeCheckedOptions = (options) => {
  const nonAllOptions = Array.isArray(options) ? options.slice(1) : [];
  const checked = nonAllOptions.filter((option) => option.checked).map((option) => option.label);
  const enabledCount = nonAllOptions.filter((option) => !option.disabled).length;
  return {
    checked,
    checkedCount: checked.length,
    enabledCount,
    isAllSelected: enabledCount > 0 ? checked.length === enabledCount : false,
  };
};

const hasAnyNonAllOptionChecked = (options) => Array.isArray(options) && options.slice(1).some((option) => option.checked && !option.disabled);

const hasAnyNonAllOptionUnchecked = (options) =>
  Array.isArray(options) && options.slice(1).some((option) => !option.checked && !option.disabled);

const buildAllCheckedFromCurrentOptions = (options) =>
  Array.isArray(options) ? options.map((option) => ({ ...option, checked: true, disabled: false })) : [];

const buildAllCheckedChecklistOptions = (allLabel, optionLabels) => {
  const sanitizedLabels = stripAllLabel(optionLabels, allLabel);
  return [{ label: allLabel, checked: true }, ...sanitizedLabels.map((label) => ({ label, checked: true }))];
};

const MAX_COHORT_BUILDER_DRAFTS = 5;
let cohortBuilderDraftSequence = 0;
const cohortBuilderDrawerDrafts = new Map();

const createCohortBuilderFlowId = () => `cohort-builder-flow-${Date.now()}-${++cohortBuilderDraftSequence}`;

const cacheCohortBuilderDrawerDraft = (flowId, draft) => {
  if (typeof flowId !== 'string' || !flowId || !draft || typeof draft !== 'object') {
    return;
  }

  cohortBuilderDrawerDrafts.delete(flowId);
  cohortBuilderDrawerDrafts.set(flowId, draft);

  while (cohortBuilderDrawerDrafts.size > MAX_COHORT_BUILDER_DRAFTS) {
    const oldestFlowId = cohortBuilderDrawerDrafts.keys().next().value;
    if (!oldestFlowId) {
      break;
    }
    cohortBuilderDrawerDrafts.delete(oldestFlowId);
  }
};

const getCohortBuilderDrawerDraft = (flowId) => {
  if (typeof flowId !== 'string' || !flowId) {
    return null;
  }

  return cohortBuilderDrawerDrafts.get(flowId) || null;
};

const clearCohortBuilderDrawerDraft = (flowId) => {
  if (typeof flowId !== 'string' || !flowId) {
    return;
  }

  cohortBuilderDrawerDrafts.delete(flowId);
};

// Convert a cohort updateTime array [year,month,day,hour,min,sec,nanos] to a
// comparable epoch.
const toCohortUpdateEpoch = (updateTime) => {
  if (!Array.isArray(updateTime) || updateTime.length < 3) return 0;
  const [year, month, day, hour = 0, minute = 0, second = 0, nanos = 0] = updateTime;
  return new Date(year, month - 1, day, hour, minute, second, Math.floor(nanos / 1000000)).getTime();
};

// Snapshot of the cohort list (luid -> updateTime epoch) taken when the user
// leaves for the Cohort Builder. On a published return it identifies the
// just-published cohort: a luid that was not in the snapshot, or one whose
// updateTime advanced past it.
const buildCohortListBaseline = (cohortPersonal, cohortPublic) => {
  const baseline = {};
  [
    ...(Array.isArray(cohortPersonal) ? cohortPersonal : []),
    ...(Array.isArray(cohortPublic) ? cohortPublic : []),
  ].forEach((item) => {
    if (item?.luid) {
      baseline[String(item.luid)] = toCohortUpdateEpoch(item.updateTime);
    }
  });
  return baseline;
};

const findPublishedCohortLuid = (cohortPersonal, cohortPublic, baseline) => {
  if (!baseline || typeof baseline !== 'object') {
    return null;
  }

  const changedItems = [
    ...(Array.isArray(cohortPersonal) ? cohortPersonal : []),
    ...(Array.isArray(cohortPublic) ? cohortPublic : []),
  ].filter((item) => item?.luid
    && (!(String(item.luid) in baseline) || toCohortUpdateEpoch(item.updateTime) > baseline[String(item.luid)]));

  if (changedItems.length === 0) {
    return null;
  }

  changedItems.sort((a, b) => toCohortUpdateEpoch(b.updateTime) - toCohortUpdateEpoch(a.updateTime));
  return changedItems[0].luid;
};

// The saved-reports list can briefly lag a just-completed Tableau publish, so a
// published return re-fetches until the baseline comparison finds the published
// cohort, bounded by these limits.
const PUBLISHED_COHORT_REFRESH_MAX_ATTEMPTS = 5;
const PUBLISHED_COHORT_REFRESH_RETRY_DELAY_MS = 1000;

const cloneChecklistOptions = (options, fallback) => {
  const source = Array.isArray(options) ? options : fallback;
  return Array.isArray(source) ? source.map((option) => ({ ...option })) : [];
};

const getCurrentChecklistLabels = (currentValue, allLabel) =>
  Array.isArray(currentValue)
    ? stripAllLabel(currentValue.slice(1).map((option) => option.label), allLabel)
    : [];

const mergeChecklistLabels = ({ allLabel, optionLabels, currentValue, preserveUnavailable }) => {
  const availableLabels = stripAllLabel(optionLabels, allLabel);

  if (!preserveUnavailable) {
    return availableLabels;
  }

  return [...getCurrentChecklistLabels(currentValue, allLabel), ...availableLabels].filter(
    (label, index, labels) => labels.indexOf(label) === index
  );
};

const hasOptionLabels = (options) => Array.isArray(options) && options.length > 0;

const monthYearToMonthStart = (value) => {
  const trimmed = String(value || '').trim();
  if (trimmed === PRIOR_COMPARISON_DISABLED_DATE) {
    return PRIOR_COMPARISON_DISABLED_DATE;
  }
  const match = /^(0[1-9]|1[0-2])\/(\d{4})$/.exec(trimmed);
  if (!match) {
    return null;
  }
  const month = match[1];
  const year = match[2];
  return `${month}/01/${year}`;
};

const parseMonthYearValue = (value) => {
  const match = /^(0[1-9]|1[0-2])\/(\d{4})$/.exec(String(value || '').trim());
  if (!match) {
    return null;
  }

  return {
    month: match[1],
    year: match[2],
  };
};

const formatMonthYearValue = (month, year) => `${String(month).padStart(2, '0')}/${year}`;

const getMonthYearFromValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const parsedMonthYear = parseMonthYearValue(value);
  if (parsedMonthYear) {
    return {
      month: Number(parsedMonthYear.month),
      year: Number(parsedMonthYear.year),
    };
  }

  const isoLikeDate = /^(\d{4})-(0[1-9]|1[0-2])-\d{2}/.exec(String(value || '').trim());
  if (isoLikeDate) {
    return {
      month: Number(isoLikeDate[2]),
      year: Number(isoLikeDate[1]),
    };
  }

  const slashDate = /^(0[1-9]|1[0-2])\/\d{2}\/(\d{4})$/.exec(String(value || '').trim());
  if (slashDate) {
    return {
      month: Number(slashDate[1]),
      year: Number(slashDate[2]),
    };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    month: date.getMonth() + 1,
    year: date.getFullYear(),
  };
};

const getMonthYearSortValue = (monthYear) => monthYear.year * 100 + monthYear.month;

const getMonthYearWithOffset = (monthYear, monthOffset) => {
  const date = new Date(monthYear.year, monthYear.month - 1 + monthOffset, 1);
  return {
    month: date.getMonth() + 1,
    year: date.getFullYear(),
  };
};

const getMonthYearValueWithOffset = (value, monthOffset) => {
  const parsed = getMonthYearFromValue(value);
  if (!parsed) {
    return null;
  }

  const offsetMonthYear = getMonthYearWithOffset(parsed, monthOffset);
  return formatMonthYearValue(offsetMonthYear.month, offsetMonthYear.year);
};

const getMonthYearOffset = (fromMonthYear, toMonthYear) => (
  (toMonthYear.year - fromMonthYear.year) * 12 + (toMonthYear.month - fromMonthYear.month)
);

const getInclusiveMonthCount = (fromMonthYear, toMonthYear) => getMonthYearOffset(fromMonthYear, toMonthYear) + 1;

const getAvailableHistoryMonthCount = (latestDate, oldestDate) => {
  const latestMonthYear = getMonthYearFromValue(latestDate);
  const oldestMonthYear = getMonthYearFromValue(oldestDate);

  if (!latestMonthYear || !oldestMonthYear) {
    return null;
  }

  const monthCount = getInclusiveMonthCount(oldestMonthYear, latestMonthYear);
  return monthCount > 0 ? monthCount : 0;
};

const isRolling12AvailableForHistory = (availableHistoryMonthCount) => availableHistoryMonthCount === null || availableHistoryMonthCount >= 12;

const isPlanYtdTimeWindow = (timeWindow) => timeWindow === 'PYTD_VS_PPYTD' || timeWindow === 'PYTD_VS_PPY';

const getPlanYearStartMonthValues = (planYearDateRanges) =>
  (Array.isArray(planYearDateRanges) ? planYearDateRanges : [])
    .map((range) => getMonthYearFromValue(range?.planStartDate))
    .filter(Boolean);

const getPlanYearStartMonthOptions = (optionValues, planYearDateRanges, oldestAvailableDate) => {
  if (optionValues.length === 0) {
    return [];
  }

  const planStartMonthYears = getPlanYearStartMonthValues(planYearDateRanges);
  if (planStartMonthYears.length === 0) {
    return [];
  }

  const optionValueSet = new Set(optionValues);
  const oldestAvailableMonthYear = getMonthYearFromValue(oldestAvailableDate);
  const selectedValues = new Set();

  planStartMonthYears.forEach((planStartMonthYear) => {
    const optionValue = formatMonthYearValue(planStartMonthYear.month, planStartMonthYear.year);

    // Tableau data restriction: the plan-start month must fall within the available Tableau months.
    if (optionValueSet.has(optionValue)) {
      selectedValues.add(optionValue);
      return;
    }

    // Lower-bound exception: for the oldest available year only, include a masterdata plan start that
    // begins before the oldest available Tableau month (that plan year is already confirmed in MD).
    if (
      oldestAvailableMonthYear
      && planStartMonthYear.year === oldestAvailableMonthYear.year
      && getMonthYearSortValue(planStartMonthYear) < getMonthYearSortValue(oldestAvailableMonthYear)
    ) {
      selectedValues.add(optionValue);
    }
  });

  return Array.from(selectedValues).sort((a, b) => getMonthSortValue(b) - getMonthSortValue(a));
};

/**
 * Trims a month range to the months that actually have data. Either bound may be
 * null to leave that side unbounded. Returns null when no month remains.
 */
const clampMonthYearRangeToAvailableData = (from, to, oldestMonthYear, latestMonthYear) => {
  if (!from || !to) {
    return null;
  }

  const boundedFrom = oldestMonthYear && getMonthYearSortValue(oldestMonthYear) > getMonthYearSortValue(from)
    ? oldestMonthYear
    : from;
  const boundedTo = latestMonthYear && getMonthYearSortValue(latestMonthYear) < getMonthYearSortValue(to)
    ? latestMonthYear
    : to;

  if (getMonthYearSortValue(boundedFrom) > getMonthYearSortValue(boundedTo)) {
    return null;
  }

  return { from: boundedFrom, to: boundedTo };
};

const isMonthYearRangeWithinAvailableData = (from, to, oldestMonthYear, latestMonthYear) => {
  const bounded = clampMonthYearRangeToAvailableData(from, to, oldestMonthYear, latestMonthYear);
  return Boolean(
    bounded
    && getMonthYearSortValue(bounded.from) === getMonthYearSortValue(from)
    && getMonthYearSortValue(bounded.to) === getMonthYearSortValue(to)
  );
};

const calculateRolling12DatesFromCurrentStart = (currentPeriodFrom, oldestAvailableDate) => {
  const parsedCurrentStart = getMonthYearFromValue(currentPeriodFrom);
  if (!parsedCurrentStart) {
    return null;
  }

  // The prior window ends the month before the current window starts, so it can never
  // run past the latest available month; only the oldest month needs to bound it.
  const boundedPriorPeriod = clampMonthYearRangeToAvailableData(
    getMonthYearWithOffset(parsedCurrentStart, -12),
    getMonthYearWithOffset(parsedCurrentStart, -1),
    getMonthYearFromValue(oldestAvailableDate),
    null
  );

  return {
    currentPeriodFrom,
    currentPeriodTo: getMonthYearValueWithOffset(currentPeriodFrom, 11),
    priorPeriodFrom: boundedPriorPeriod ? formatMonthYearValue(boundedPriorPeriod.from.month, boundedPriorPeriod.from.year) : '',
    priorPeriodTo: boundedPriorPeriod ? formatMonthYearValue(boundedPriorPeriod.to.month, boundedPriorPeriod.to.year) : '',
  };
};

const normalizeRolling12DatesForAvailableHistory = (timeWindowDateValues, oldestAvailableDate) => {
  if (!timeWindowDateValues?.currentPeriodFrom) {
    return timeWindowDateValues;
  }

  return calculateRolling12DatesFromCurrentStart(timeWindowDateValues.currentPeriodFrom, oldestAvailableDate) || timeWindowDateValues;
};

const toDateRangeValues = (timeWindowDateValues) => ({
  currentPeriodFromValue: timeWindowDateValues?.currentPeriodFrom || defaultAppliedFilterValues.currentPeriodFrom,
  currentPeriodToValue: timeWindowDateValues?.currentPeriodTo || defaultAppliedFilterValues.currentPeriodTo,
  priorPeriodFromValue: timeWindowDateValues?.priorPeriodFrom || defaultAppliedFilterValues.priorPeriodFrom,
  priorPeriodToValue: timeWindowDateValues?.priorPeriodTo || defaultAppliedFilterValues.priorPeriodTo,
});

const normalizeRolling12DateRangeValues = (dateRangeValues, oldestAvailableDate) => {
  const normalizedTimeWindowDates = calculateRolling12DatesFromCurrentStart(dateRangeValues?.currentPeriodFromValue, oldestAvailableDate);
  return normalizedTimeWindowDates ? toDateRangeValues(normalizedTimeWindowDates) : dateRangeValues;
};

/**
 * Calculate prior period dates for Custom Range based on current period and available data.
 * For current periods < 12 months: uses available months before current period.
 * For current periods >= 12 months: uses 12 months prior, clamped to available data.
 *
 * @param {string} currentPeriodFrom - Current period start (MM/YYYY format)
 * @param {string} currentPeriodTo - Current period end (MM/YYYY format)
 * @param {string} latestDate - Latest available date
 * @param {string} oldestDate - Oldest available date
 * @returns {{ priorPeriodFrom: string, priorPeriodTo: string }} Prior dates or empty strings if no prior data
 */
const calculateCustomRangePriorDates = (currentPeriodFrom, currentPeriodTo, latestDate, oldestDate) => {
  const parsedCurrentStart = getMonthYearFromValue(currentPeriodFrom);
  const parsedCurrentEnd = getMonthYearFromValue(currentPeriodTo);
  const parsedOldest = getMonthYearFromValue(oldestDate);

  if (!parsedCurrentStart || !parsedCurrentEnd || !parsedOldest) {
    return { priorPeriodFrom: '', priorPeriodTo: '' };
  }

  // Calculate the current period duration
  const currentPeriodMonths = getInclusiveMonthCount(parsedCurrentStart, parsedCurrentEnd);

  // Prior period ends one month before current period starts
  const priorPeriodEnd = getMonthYearWithOffset(parsedCurrentStart, -1);

  // If current period starts at or before oldest available month, no prior data exists
  if (getMonthYearSortValue(parsedCurrentStart) <= getMonthYearSortValue(parsedOldest)) {
    return { priorPeriodFrom: '', priorPeriodTo: '' };
  }

  // Calculate prior period start: 
  // - If current period is less than 12 months, prior starts at oldest available month
  // - If current period is 12+ months, prior starts 12 months before current start (or oldest, whichever is later)
  let priorPeriodStart;
  if (currentPeriodMonths < 12) {
    priorPeriodStart = parsedOldest;
  } else {
    const priorStart12Months = getMonthYearWithOffset(parsedCurrentStart, -12);
    priorPeriodStart = getMonthYearSortValue(parsedOldest) > getMonthYearSortValue(priorStart12Months)
      ? parsedOldest
      : priorStart12Months;
  }

  // Validate that prior period start is before prior period end
  if (getMonthYearSortValue(priorPeriodStart) > getMonthYearSortValue(priorPeriodEnd)) {
    return { priorPeriodFrom: '', priorPeriodTo: '' };
  }

  return {
    priorPeriodFrom: formatMonthYearValue(priorPeriodStart.month, priorPeriodStart.year),
    priorPeriodTo: formatMonthYearValue(priorPeriodEnd.month, priorPeriodEnd.year),
  };
};

const getMonthRangeCount = (periodFrom, periodTo) => {
  const parsedPeriodFrom = getMonthYearFromValue(periodFrom);
  const parsedPeriodTo = getMonthYearFromValue(periodTo);

  if (!parsedPeriodFrom || !parsedPeriodTo) {
    return 0;
  }

  const monthCount = getInclusiveMonthCount(parsedPeriodFrom, parsedPeriodTo);
  return monthCount > 0 ? monthCount : 0;
};

const toIsoMonthStart = (monthYear) => `${monthYear.year}-${String(monthYear.month).padStart(2, '0')}-01`;

/**
 * Plan YTD dates for a user-selected plan-year start. This is the shared time-window
 * calculation with the selected month standing in for the masterdata plan start, so
 * the dropdown preview and the default window can never disagree.
 */
const calculatePlanYtdDatesFromCurrentStart = (currentPeriodFrom, timeWindow, latestDate, planYearDateRanges, oldestDate) => {
  const parsedCurrentStart = getMonthYearFromValue(currentPeriodFrom);
  if (!parsedCurrentStart || !isPlanYtdTimeWindow(timeWindow)) {
    return null;
  }

  return calculateTimeWindowDates(
    timeWindow,
    latestDate,
    toIsoMonthStart(parsedCurrentStart),
    planYearDateRanges,
    oldestDate
  );
};

const getMonthYearOptionsThrough = (optionValues, maxValue) => {
  const parsedMaxValue = getMonthYearFromValue(maxValue);
  if (!parsedMaxValue) {
    return optionValues;
  }

  const maxSortValue = getMonthYearSortValue(parsedMaxValue);
  return optionValues.filter((optionValue) => {
    const parsedOptionValue = getMonthYearFromValue(optionValue);
    return parsedOptionValue && getMonthYearSortValue(parsedOptionValue) <= maxSortValue;
  });
};

const buildMonthYearOptions = (latestDate, oldestDate) => {
  const latestMonthYear = getMonthYearFromValue(latestDate);
  if (!latestMonthYear) {
    return [];
  }

  const upperMonthYear = latestMonthYear;

  const parsedOldestMonthYear = getMonthYearFromValue(oldestDate);
  const lowerMonthYear = parsedOldestMonthYear && getMonthYearSortValue(parsedOldestMonthYear) <= getMonthYearSortValue(upperMonthYear)
    ? parsedOldestMonthYear
    : getMonthYearWithOffset(upperMonthYear, -36);
  const lowerSortValue = getMonthYearSortValue(lowerMonthYear);

  const options = [];
  const optionSet = new Set();

  const addOption = (month, year) => {
    const nextValue = formatMonthYearValue(month, year);
    if (!optionSet.has(nextValue)) {
      optionSet.add(nextValue);
      options.push(nextValue);
    }
  };

  let cursor = upperMonthYear;
  while (getMonthYearSortValue(cursor) >= lowerSortValue) {
    addOption(cursor.month, cursor.year);
    cursor = getMonthYearWithOffset(cursor, -1);
  }

  return options.sort((a, b) => getMonthSortValue(b) - getMonthSortValue(a));
};

const buildChecklistOptions = ({
  allLabel,
  optionLabels,
  currentValue,
  isTouched,
  selectedLabelsOverride,
  preserveUnavailable = false,
  baselineOptionLabels,
  forceSelectionOverride = false,
}) => {
  const selectedLabelsFromCurrent = Array.isArray(currentValue)
    ? currentValue.slice(1).filter((option) => option.checked).map((option) => option.label)
    : [];

  const hasOverrideLabels = Array.isArray(selectedLabelsOverride) && selectedLabelsOverride.length > 0;
  const shouldUseOverrideLabels =
    hasOverrideLabels &&
    (forceSelectionOverride || (!isTouched && selectedLabelsFromCurrent.length === 0));

  const selectedLabels = shouldUseOverrideLabels ? selectedLabelsOverride : selectedLabelsFromCurrent;

  const selectedLabelSet = new Set(selectedLabels);
  const hasAnySelected = selectedLabels.length > 0;
  // When restoring a previewed selection, honor it verbatim: a non-empty override
  // checks exactly those labels, while an empty override means "all selected".
  const shouldDefaultAll = forceSelectionOverride ? !hasOverrideLabels : !isTouched && !hasAnySelected;

  const sanitizedLabels = stripAllLabel(optionLabels, allLabel);
  const availableLabelSet = new Set(sanitizedLabels);
  const hydratedLabels = preserveUnavailable && hasOptionLabels(baselineOptionLabels)
    ? stripAllLabel(baselineOptionLabels, allLabel)
    : mergeChecklistLabels({ allLabel, optionLabels, currentValue, preserveUnavailable });
  const hydratedSubOptions = hydratedLabels.map((label) => {
    const isUnavailable = preserveUnavailable && !availableLabelSet.has(label);

    return {
      label,
      checked: isUnavailable ? false : shouldDefaultAll ? true : selectedLabelSet.has(label),
      ...(isUnavailable ? { disabled: true } : {}),
    };
  });

  const enabledSubOptions = hydratedSubOptions.filter((option) => !option.disabled);
  const areAllSubOptionsChecked = enabledSubOptions.length > 0 && enabledSubOptions.every((option) => option.checked);
  const allChecked = shouldDefaultAll ? true : areAllSubOptionsChecked;

  return [{ label: allLabel, checked: allChecked }, ...hydratedSubOptions];
};

const buildOptionsDatePayload = ({
  currentPeriodFromValue,
  currentPeriodToValue,
  priorPeriodFromValue,
  priorPeriodToValue,
}) => {
  const currentStart = monthYearToMonthStart(currentPeriodFromValue);
  const currentEnd = monthYearToMonthStart(currentPeriodToValue);
  const priorStart = monthYearToMonthStart(priorPeriodFromValue);
  const priorEnd = monthYearToMonthStart(priorPeriodToValue);

  return {
    ...(currentStart ? { currentStart } : {}),
    ...(currentEnd ? { currentEnd } : {}),
    ...(priorStart ? { priorStart } : {}),
    ...(priorEnd ? { priorEnd } : {}),
  };
};

const buildOptionsBasePayload = ({
  tenantCd,
  dateBasisValue,
  currentPeriodFromValue,
  currentPeriodToValue,
  priorPeriodFromValue,
  priorPeriodToValue,
}) => ({
  tenantCd,
  dateBasis: dateBasisValue,
  ...buildOptionsDatePayload({
    currentPeriodFromValue,
    currentPeriodToValue,
    priorPeriodFromValue,
    priorPeriodToValue,
  }),
});

const dateBasisOptions = [
  { label: 'Paid Date', value: 'paid' },
  { label: 'Incurred Date', value: 'incurred' }
];

const INCURRED_DATE_BASIS_GUIDANCE = 'Due to claim processing timelines, it is recommended to backdate the Incurred Date Basis by two months.';

const toggleSelectionOptions = (options, index) => {
  if (index === 0 && options.length > 1) {
    const enabledSubOptions = options.slice(1).filter((option) => !option.disabled);
    const areAllEnabledOptionsChecked = enabledSubOptions.length > 0 && enabledSubOptions.every((option) => option.checked);
    const nextChecked = !areAllEnabledOptionsChecked;

    return options.map((option, optionIndex) => ({
      ...option,
      checked: optionIndex === 0 || !option.disabled ? nextChecked : false,
    }));
  }

  if (options[index]?.disabled) {
    return options;
  }

  const nextOptions = options.map((option, optionIndex) =>
    optionIndex === index
      ? {
        ...option,
        checked: !option.checked,
      }
      : option,
  );

  const enabledSubOptions = nextOptions.slice(1).filter((option) => !option.disabled);
  const areAllSelected = enabledSubOptions.length > 0 && enabledSubOptions.every((option) => option.checked);

  return nextOptions.map((option, optionIndex) =>
    optionIndex === 0
      ? {
        ...option,
        checked: areAllSelected,
      }
      : option,
  );
};

const PERSON_ID_MAX_LENGTH = 500000;
const COHORT_PERSON_ID_FETCH_ERROR = 'Unable to load person IDs for the selected cohort.';
const PERSON_ID_VALIDATION_ERROR = 'Unable to validate Person IDs right now. Please try again.';

const isValidPersonIdValue = (value) => {
  if (!value.trim()) {
    return true;
  }

  const entries = value
    .split(/[;,\n]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  return entries.length > 0 && entries.every((entry) => /^[a-zA-Z0-9]+$/.test(entry));
};

const isValidMonthValue = (value) => /^(0[1-9]|1[0-2])\/\d{4}$/.test(value.trim());

const getMonthSortValue = (value) => {
  const [month, year] = value.trim().split('/');
  return Number(year) * 100 + Number(month);
};

const getToMonthYearOptions = (optionValues, fromValue) => {
  if (!isValidMonthValue(String(fromValue || ''))) {
    return optionValues;
  }

  const fromSortValue = getMonthSortValue(fromValue);
  return optionValues.filter((optionValue) => getMonthSortValue(optionValue) >= fromSortValue);
};

const getFromMonthYearOptions = (optionValues, toValue) => {
  if (!isValidMonthValue(String(toValue || ''))) {
    return optionValues;
  }

  const toSortValue = getMonthSortValue(toValue);
  return optionValues.filter((optionValue) => getMonthSortValue(optionValue) <= toSortValue);
};

const getBeforeMonthYearOptions = (optionValues, beforeValue) => {
  if (!isValidMonthValue(String(beforeValue || ''))) {
    return optionValues;
  }

  const beforeSortValue = getMonthSortValue(beforeValue);
  return optionValues.filter((optionValue) => getMonthSortValue(optionValue) < beforeSortValue);
};

const getAfterMonthYearOptions = (optionValues, afterValue) => {
  if (!isValidMonthValue(String(afterValue || ''))) {
    return optionValues;
  }

  const afterSortValue = getMonthSortValue(afterValue);
  return optionValues.filter((optionValue) => getMonthSortValue(optionValue) > afterSortValue);
};

const hasAnyDateRangeValue = (dateValues) => Boolean(
  dateValues?.currentPeriodFromValue
  || dateValues?.currentPeriodToValue
  || dateValues?.priorPeriodFromValue
  || dateValues?.priorPeriodToValue
);

const getDateRangeValidationResult = ({
  currentPeriodFromValue,
  currentPeriodToValue,
  priorPeriodFromValue,
  priorPeriodToValue,
}, options = {}) => {
  const allowEmptyPriorPeriod = Boolean(options.allowEmptyPriorPeriod);
  const isPriorPeriodEmpty = !priorPeriodFromValue.trim() && !priorPeriodToValue.trim();
  const invalidFields = {
    currentPeriodFrom: !currentPeriodFromValue.trim() || !isValidMonthValue(currentPeriodFromValue),
    currentPeriodTo: !currentPeriodToValue.trim() || !isValidMonthValue(currentPeriodToValue),
    priorPeriodFrom: allowEmptyPriorPeriod && isPriorPeriodEmpty ? false : !priorPeriodFromValue.trim() || !isValidMonthValue(priorPeriodFromValue),
    priorPeriodTo: allowEmptyPriorPeriod && isPriorPeriodEmpty ? false : !priorPeriodToValue.trim() || !isValidMonthValue(priorPeriodToValue),
  };

  const hasInvalidFormat = Object.values(invalidFields).some(Boolean);
  if (hasInvalidFormat) {
    return {
      message: FORMAT_ERROR,
      invalidFields,
    };
  }

  const hasInvalidDateOrder = allowEmptyPriorPeriod && isPriorPeriodEmpty
    ? !(getMonthSortValue(currentPeriodFromValue) <= getMonthSortValue(currentPeriodToValue))
    : !(
      getMonthSortValue(priorPeriodFromValue) <= getMonthSortValue(priorPeriodToValue)
      && getMonthSortValue(priorPeriodToValue) < getMonthSortValue(currentPeriodFromValue)
      && getMonthSortValue(currentPeriodFromValue) <= getMonthSortValue(currentPeriodToValue)
    );

  if (hasInvalidDateOrder) {
    return {
      message: DATE_ORDER_ERROR,
      invalidFields: {},
    };
  }

  return {
    message: null,
    invalidFields: {},
  };
};

const DATE_RANGE_FIELD_ERROR_KEYS = {
  currentPeriodFromValue: 'currentPeriodFrom',
  currentPeriodToValue: 'currentPeriodTo',
  priorPeriodFromValue: 'priorPeriodFrom',
  priorPeriodToValue: 'priorPeriodTo',
};

const isPriorPeriodDateFieldName = (fieldName) => fieldName === 'priorPeriodFromValue' || fieldName === 'priorPeriodToValue';

const getDateOptionError = (fieldName, value, optionValues = []) => {
  const normalizedValue = String(value || '').trim();
  if (!isValidMonthValue(normalizedValue) || optionValues.length === 0 || optionValues.includes(normalizedValue)) {
    return null;
  }

  return {
    fieldName,
    value: normalizedValue,
    errorKey: DATE_RANGE_FIELD_ERROR_KEYS[fieldName],
  };
};

const CHECKLIST_SCROLL_THRESHOLD = 5;
// Sized so the header, search box, selected-summary row and four option rows fit without scrolling.
const CHECKLIST_SCROLL_MAX_HEIGHT = 400;
const CHECKLIST_SCROLLBAR_WIDTH = 4;
const CHECKLIST_SCROLLBAR_THUMB_HEIGHT = 56;
const CHECKLIST_SCROLLBAR_GAP = 8;
const DIVISIONS_PLANS_HELPER_TEXT = 'Select the divisions and plans to include in your results. Available options may change based on your other filter selections.';
const DIVISION_UNAVAILABLE_WARNING_TEXT = 'Some options are unavailable based on your Plan selections.';
const PLAN_UNAVAILABLE_WARNING_TEXT = 'Some options are unavailable based on your Division selections.';
const R12_PRIOR_PERIOD_WARNING_TEXT = 'The prior period contains less than 12 months of data.';
const PRIOR_COMPARISON_GUIDANCE_TEXT = 'When turned off, comparisons to prior period will not be included across BenInsights.';
const PRIOR_COMPARISON_UNAVAILABLE_TEXT = 'Adjust the Current Period dates to enable Prior Period comparison.';

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const checklistScrollViewportSx = {
  position: 'relative',
  flex: '1 1 auto',
  minHeight: 0,
};

const checklistScrollContentSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: `${CHECKLIST_SCROLLBAR_WIDTH + CHECKLIST_SCROLLBAR_GAP}px`,
  bottom: 0,
  overflowY: 'auto',
  pr: 1,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    width: 0,
    height: 0,
  },
};

const checklistScrollTrackSx = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: `${CHECKLIST_SCROLLBAR_WIDTH}px`,
  borderRadius: '999px',
  backgroundColor: 'transparent',
};

const getChecklistScrollbarThumbSx = (thumbTop, isDragging) => ({
  position: 'absolute',
  top: `${thumbTop}px`,
  left: 0,
  width: `${CHECKLIST_SCROLLBAR_WIDTH}px`,
  height: `${CHECKLIST_SCROLLBAR_THUMB_HEIGHT}px`,
  borderRadius: '999px',
  backgroundColor: colors.borderLight,
  cursor: 'grab',
  ...(isDragging
    ? {
      cursor: 'grabbing',
    }
    : {}),
});

const CheckboxList = ({ errorText, options, onToggle, searchQuery, onSearchQueryChange, sectionLabel, selectedSummaryLabel, showFirstItemSeparator = false, unavailableWarningText, isRefreshing = false }) => {
  const subOptions = options.slice(1);
  const hasSubOptions = subOptions.length > 0;
  const enabledSubOptions = subOptions.filter((option) => !option.disabled);
  const isAnySubOptionChecked = enabledSubOptions.some((option) => option.checked);
  const areAllSubOptionsChecked = enabledSubOptions.length > 0 && enabledSubOptions.every((option) => option.checked);
  const selectedSubOptionCount = enabledSubOptions.filter((option) => option.checked).length;
  const hasUnavailableOptions = subOptions.some((option) => option.disabled);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchTargetLabel = String(sectionLabel || '').toLowerCase();
  // Filtering only hides rows; every row keeps its index in `options` so selections survive a search.
  const visibleOptionEntries = React.useMemo(() => {
    const entries = hasSubOptions
      ? subOptions.map((option, subOptionIndex) => ({ option, index: subOptionIndex + 1 }))
      : options.map((option, optionIndex) => ({ option, index: optionIndex }));

    if (!normalizedSearchQuery) {
      return entries;
    }

    return entries.filter(({ option }) => String(option.label || '').toLowerCase().includes(normalizedSearchQuery));
  }, [hasSubOptions, normalizedSearchQuery, options, subOptions]);
  const hasNoSearchResults = Boolean(normalizedSearchQuery) && visibleOptionEntries.length === 0;
  // Derived from the unfiltered list: letting the search result count flip this would swap
  // the scrolling/non-scrolling branches below and remount the search field, losing focus.
  const totalOptionEntryCount = hasSubOptions ? subOptions.length : options.length;
  const shouldScroll = totalOptionEntryCount + (hasSubOptions ? 1 : 0) > CHECKLIST_SCROLL_THRESHOLD;

  const scrollRef = React.useRef(null);
  const trackRef = React.useRef(null);
  const [thumbTop, setThumbTop] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStartYRef = React.useRef(0);
  const dragStartThumbTopRef = React.useRef(0);

  const updateThumbFromScroll = React.useCallback(() => {
    if (!shouldScroll) return;

    const scrollEl = scrollRef.current;
    const trackEl = trackRef.current;
    if (!scrollEl || !trackEl) return;

    const maxScrollTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
    const maxThumbTop = Math.max(0, trackEl.clientHeight - CHECKLIST_SCROLLBAR_THUMB_HEIGHT);

    if (maxScrollTop === 0 || maxThumbTop === 0) {
      setThumbTop(0);
      return;
    }

    setThumbTop((scrollEl.scrollTop / maxScrollTop) * maxThumbTop);
  }, [shouldScroll]);

  React.useEffect(() => {
    if (!shouldScroll) return undefined;

    const scrollEl = scrollRef.current;
    if (!scrollEl) return undefined;

    updateThumbFromScroll();

    scrollEl.addEventListener('scroll', updateThumbFromScroll, { passive: true });
    window.addEventListener('resize', updateThumbFromScroll);

    return () => {
      scrollEl.removeEventListener('scroll', updateThumbFromScroll);
      window.removeEventListener('resize', updateThumbFromScroll);
    };
  }, [shouldScroll, updateThumbFromScroll, visibleOptionEntries.length]);

  React.useEffect(() => {
    if (!isDragging) return undefined;

    const handleMouseMove = (event) => {
      const scrollEl = scrollRef.current;
      const trackEl = trackRef.current;
      if (!scrollEl || !trackEl) return;

      const maxScrollTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
      const maxThumbTop = Math.max(0, trackEl.clientHeight - CHECKLIST_SCROLLBAR_THUMB_HEIGHT);
      if (maxScrollTop === 0 || maxThumbTop === 0) return;

      const deltaY = event.clientY - dragStartYRef.current;
      const nextThumbTop = clampNumber(dragStartThumbTopRef.current + deltaY, 0, maxThumbTop);
      setThumbTop(nextThumbTop);
      scrollEl.scrollTop = (nextThumbTop / maxThumbTop) * maxScrollTop;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleThumbMouseDown = (event) => {
    event.preventDefault();
    dragStartYRef.current = event.clientY;
    dragStartThumbTopRef.current = thumbTop;
    setIsDragging(true);
  };

  const handleTrackMouseDown = (event) => {
    const scrollEl = scrollRef.current;
    const trackEl = trackRef.current;
    if (!scrollEl || !trackEl) return;

    const maxScrollTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
    const maxThumbTop = Math.max(0, trackEl.clientHeight - CHECKLIST_SCROLLBAR_THUMB_HEIGHT);
    if (maxScrollTop === 0 || maxThumbTop === 0) return;

    const trackRect = trackEl.getBoundingClientRect();
    const clickOffset = event.clientY - trackRect.top;
    const centeredThumbTop = clickOffset - CHECKLIST_SCROLLBAR_THUMB_HEIGHT / 2;
    const nextThumbTop = clampNumber(centeredThumbTop, 0, maxThumbTop);
    setThumbTop(nextThumbTop);
    scrollEl.scrollTop = (nextThumbTop / maxThumbTop) * maxScrollTop;
  };

  const renderOption = (option, index) => {
    const isAllOption = index === 0 && hasSubOptions;
    const checked = isAllOption ? areAllSubOptionsChecked : option.checked;
    const indeterminate = isAllOption ? isAnySubOptionChecked && !areAllSubOptionsChecked : false;
    const hasSeparator = Boolean(showFirstItemSeparator && index === 0);
    const label = isAllOption && selectedSummaryLabel ? `${selectedSummaryLabel} (${selectedSubOptionCount})` : option.label;
    const isDisabled = Boolean(option.disabled);

    return (
      <Box
        key={`${option.label}-${index}`}
        data-testid={`checkboxlist-option-${option.label}`}
        data-is-first-option={index === 0 ? 'true' : 'false'}
        data-has-separator={hasSeparator ? 'true' : 'false'}
        sx={
          showFirstItemSeparator && index === 0
            ? {
              width: '100%',
              borderBottom: `1px solid ${colors.border}`,
              pb: 3,
            }
            : undefined
        }>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              disabled={isDisabled}
              indeterminate={indeterminate}
              onChange={() => onToggle(index)}
              inputProps={isAllOption ? { 'aria-label': option.label } : undefined}
              sx={checkboxSx}
            />
          }
          label={<Typography sx={isDisabled ? { ...optionLabelSx, ...disabledOptionLabelSx } : optionLabelSx}>{label}</Typography>}
          sx={{ alignItems: 'center', m: 0, gap: 1 }}
        />
      </Box>
    );
  };

  const selectedSummaryOption = hasSubOptions ? renderOption(options[0], 0) : null;
  const sectionHeading = sectionLabel ? (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} width="100%">
      <Typography sx={optionLabelSx}>{sectionLabel}</Typography>
      {isRefreshing ? (
        <CircularProgress
          aria-label={`${sectionLabel} options loading`}
          data-testid="checkboxlist-refreshing-indicator"
          size={16}
          thickness={4}
          sx={{ color: colors.primary }}
        />
      ) : null}
    </Box>
  ) : null;
  const searchField = hasSubOptions ? (
    <TextField
      variant="outlined"
      value={searchQuery}
      onChange={(event) => onSearchQueryChange(event.target.value)}
      placeholder={`Search ${searchTargetLabel}`}
      data-testid={`checkboxlist-search-${searchTargetLabel}`}
      sx={checklistSearchFieldSx}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0 }}>
            <IconSearch aria-hidden="true" stroke={2} size={20} color={colors.border} />
          </InputAdornment>
        ),
      }}
      inputProps={{ 'aria-label': `Search ${searchTargetLabel}` }}
    />
  ) : null;
  const optionRowsContent = hasNoSearchResults ? (
    <Typography data-testid="checkboxlist-empty-search-message" sx={checklistEmptyStateSx}>
      {`No ${searchTargetLabel} match your search`}
    </Typography>
  ) : (
    <>
      {visibleOptionEntries.map(({ option, index }) => renderOption(option, index))}
    </>
  );

  return (
    <Box display="flex" flexDirection="column" gap={1} width="100%">
      {shouldScroll ? (
        <Box
          sx={{
            ...getSelectionPanelSx(Boolean(errorText)),
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: CHECKLIST_SCROLL_MAX_HEIGHT,
            overflow: 'hidden',
          }}>
          {sectionHeading}
          {searchField}
          {selectedSummaryOption}
          <Box sx={checklistScrollViewportSx}>
            <Box
              ref={scrollRef}
              data-testid="checkboxlist-scroll-options"
              sx={checklistScrollContentSx}
              display="flex"
              flexDirection="column"
              gap={3}>
              {optionRowsContent}
            </Box>
            <Box ref={trackRef} sx={checklistScrollTrackSx} onMouseDown={handleTrackMouseDown}>
              <Box
                role="scrollbar"
                tabIndex={-1}
                aria-hidden="true"
                onMouseDown={handleThumbMouseDown}
                sx={getChecklistScrollbarThumbSx(thumbTop, isDragging)}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={getSelectionPanelSx(Boolean(errorText))}>
          <Box display="flex" flexDirection="column" gap={2}>
            {sectionHeading}
            {searchField}
            {selectedSummaryOption}
            <Box display="flex" flexDirection="column" gap={3}>
              {optionRowsContent}
            </Box>
          </Box>
        </Box>
      )}
      {hasUnavailableOptions && unavailableWarningText ? (
        <Box display="flex" alignItems="center" gap={0.5} width="100%" data-testid="checkboxlist-unavailable-warning">
          <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: colors.text }} />
          <Typography sx={warningTextSx}>{unavailableWarningText}</Typography>
        </Box>
      ) : null}
      {errorText ? <Typography sx={errorTextSx}>{errorText}</Typography> : null}
    </Box>
  );
};

const DateFieldSelector = ({ ariaLabel, hasError, isLocked, onBlur, onChange, onEditingChange, optionValues, value, lockMessage = null }) => {
  if (isLocked) {
    return (
      <Box
        aria-label={ariaLabel}
        role="textbox"
        aria-readonly="true"
        aria-disabled="true"
        tabIndex={0}
        data-testid={`locked-date-field-${ariaLabel}`}
        sx={lockedDateFieldSx}>
        <Typography sx={lockedDateTextSx}>{value || '--/----'}</Typography>
        <Box sx={lockedDateControlSx}>
          <StyledTooltip title={lockMessage} placement="bottom">
            <LockOutlinedIcon aria-hidden="true" sx={{ fontSize: 16, color: colors.text }} />
          </StyledTooltip>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" width="100%" data-testid={`date-field-selector-${ariaLabel}`}>
      <Autocomplete
        freeSolo
        disableClearable
        disablePortal
        forcePopupIcon
        options={optionValues}
        value={value || ''}
        inputValue={value || ''}
        onChange={(event, nextValue) => {
          onEditingChange?.(false);
          onChange(nextValue || '', { source: 'option' });
        }}
        onInputChange={(event, nextInputValue, reason) => {
          if (reason === 'input' || reason === 'clear') {
            onEditingChange?.(true);
            onChange(nextInputValue, { source: 'input' });
          }
        }}
        onClose={(event, reason) => {
          if (reason === 'blur' || reason === 'escape' || reason === 'toggleInput') {
            onEditingChange?.(false);
            onBlur?.();
          }
        }}
        fullWidth
        popupIcon={<IconChevronDown stroke={2} size={20} color={colors.iconFill} />}
        slotProps={{
          paper: { sx: getDateDropdownPaperSx() },
        }}
        renderOption={(renderProps, optionValue) => {
          const { className, key, ...optionProps } = renderProps;

          return (
            <Box
              component="li"
              key={key ?? optionValue}
              {...optionProps}
              sx={getDateDropdownOptionSx(value === optionValue)}>
              {optionValue}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            sx={getDatePartSelectSx(hasError)}
            inputProps={{
              ...params.inputProps,
              'aria-label': ariaLabel,
              onBlur: (event) => {
                params.inputProps.onBlur?.(event);
                onEditingChange?.(false);
                onBlur?.();
              },
            }}
          />
        )}
      />
    </Box>
  );
};

const LoadingPanel = () => (
  <Box sx={loadingPanelSx}>
    <CircularProgress size={40} thickness={3.6} sx={{ color: colors.primary }} />
  </Box>
);

const GlobalFiltersDrawer = ({ appliedFilters, companies, currentTenantCd, cohortBuilderFlowId, cohortBuilderTenantCd, filterOptions, isReportView, onApply, onClose, open, showTenantFilter, returnFromCohortBuilder, cohortWasPublished, onCohortBuilderReturnConsumed }) => {
  const dispatch = useDispatch();
  const reduxStore = useStore();
  const filtersScrollRef = React.useRef(null);
  const timeWindowDates = useSelector((state) => state.fo?.timeWindowDates);
  const timeWindowDatesTimeWindow = useSelector((state) => state.fo?.timeWindowDatesTimeWindow);
  const availableDates = useSelector((state) => state.fo?.availableDates);
  const oldestAvailableDate = useSelector((state) => state.fo?.oldestAvailableDate);
  const planStartDate = useSelector((state) => state.fo?.planStartDates);
  const planYearDateRanges = useSelector((state) => state.fo?.planYearDateRanges);
  const globalFilterOptions = useSelector((state) => state.fo?.globalFilterOptions);
  const isGlobalFilterOptionsLoading = useSelector((state) => state.fo?.isGlobalFilterOptionsLoading);
  const globalFilterOptionsRequestKey = useSelector((state) => state.fo?.globalFilterOptionsRequestKey);
  const globalFilterOptionsLastSuccessKey = useSelector((state) => state.fo?.globalFilterOptionsLastSuccessKey);
  const globalFilterOptionsCanceledKey = useSelector((state) => state.fo?.globalFilterOptionsCanceledKey);
  const currentAppCode = useSelector((state) => state.user?.currentApp);
  const cohortOptions = useSelector((state) => state.fo?.cohortOptions);
  const cohortPersonalRaw = useSelector((state) => state.fo?.cohortPersonal);
  const cohortPublicRaw = useSelector((state) => state.fo?.cohortPublic);
  const cohortOptionsTenantCd = useSelector((state) => state.fo?.cohortOptionsTenantCd);

  const currentTenant = Array.isArray(companies) ? companies.find((company) => company.code === currentTenantCd) || null : null;
  const [tenantValue, setTenantValue] = React.useState(currentTenant);
  const [hasTenantDraftSelection, setHasTenantDraftSelection] = React.useState(false);
  const effectiveTenantValue = hasTenantDraftSelection ? tenantValue : currentTenant || tenantValue;
  const areTenantFiltersEnabled = effectiveTenantValue?.filtersEnabled !== false;
  const isDateBasisEnabled = effectiveTenantValue?.dateBasisEnabled !== false;
  const isDateRangeEnabled = effectiveTenantValue?.dateRangeEnabled !== false;
  const areDivisionsEnabled = effectiveTenantValue?.divisionsEnabled !== false;
  const arePlansEnabled = effectiveTenantValue?.plansEnabled !== false;
  const isCohortBuilderEnabled = effectiveTenantValue?.cohortBuilderEnabled !== false;
  const [divisionValue, setDivisionValue] = React.useState(divisionOptions.map((option) => ({ ...option })));
  const [planValue, setPlanValue] = React.useState(planOptions.map((option) => ({ ...option })));
  const [divisionSearchQuery, setDivisionSearchQuery] = React.useState('');
  const [planSearchQuery, setPlanSearchQuery] = React.useState('');
  const [didClearAll, setDidClearAll] = React.useState(false);
  const [dateBasisValue, setDateBasisValue] = React.useState(defaultAppliedFilterValues.dateBasis);
  const [currentPeriodFromValue, setCurrentPeriodFromValue] = React.useState(defaultAppliedFilterValues.currentPeriodFrom);
  const [currentPeriodToValue, setCurrentPeriodToValue] = React.useState(defaultAppliedFilterValues.currentPeriodTo);
  const [personIdValue, setPersonIdValue] = React.useState(defaultAppliedFilterValues.personIdValue);
  const [priorPeriodFromValue, setPriorPeriodFromValue] = React.useState(defaultAppliedFilterValues.priorPeriodFrom);
  const [priorPeriodToValue, setPriorPeriodToValue] = React.useState(defaultAppliedFilterValues.priorPeriodTo);
  const [enablePriorComparison, setEnablePriorComparison] = React.useState(appliedFilters?.enablePriorComparison !== false);
  const [timeWindowValue, setTimeWindowValue] = React.useState(defaultAppliedFilterValues.timeWindow);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [personIdValidationMessage, setPersonIdValidationMessage] = React.useState('');
  const [isPersonIdValidationLoading, setIsPersonIdValidationLoading] = React.useState(false);
  const [isDivisionTouched, setIsDivisionTouched] = React.useState(false);
  const [isPlanTouched, setIsPlanTouched] = React.useState(false);
  const [cascadeMode, setCascadeMode] = React.useState(null);
  const [isTenantOpen, setIsTenantOpen] = React.useState(false);
  const [isTimeWindowOpen, setIsTimeWindowOpen] = React.useState(false);
  const [isDrawerInitialized, setIsDrawerInitialized] = React.useState(false);
  const [availableDateBoundsRequestStatus, setAvailableDateBoundsRequestStatus] = React.useState({
    contextKey: null,
    isLoading: false,
  });
  const [cohortValue, setCohortValue] = React.useState('');
  const [isCohortPersonIdsLoading, setIsCohortPersonIdsLoading] = React.useState(false);
  const [populationMode, setPopulationMode] = React.useState(null);
  const [publishedCohortRefreshTenantCd, setPublishedCohortRefreshTenantCd] = React.useState(null);
  const [focusCohortSelectionRequestKey, setFocusCohortSelectionRequestKey] = React.useState(null);
  const [isSavedFilterPreviewLoading, setIsSavedFilterPreviewLoading] = React.useState(false);
  // Holds the saved division/plan label selections previewed for a tenant chosen
  // in the drawer (before Apply). When set, the option hydration restores these
  // selections instead of defaulting every option to checked on tenant change.
  const [previewedSelection, setPreviewedSelection] = React.useState(null);
  // Keyed, additive cache of unfiltered ("baseline") option responses, indexed by
  // the base options payload (tenant + dateBasis + date range, with no cascade
  // selection). Keeping it additive and keyed means opening/closing the drawer
  // never discards a previously fetched baseline: the baseline for the current
  // context is a pure lookup, which is what keeps unavailable options greyed out
  // instead of removed.
  const [optionsBaselineCache, setOptionsBaselineCache] = React.useState({});
  const cacheBaselineOptions = React.useCallback((key, options) => {
    if (!key) {
      return;
    }
    setOptionsBaselineCache((prev) => (prev[key] === options ? prev : { ...prev, [key]: options }));
  }, []);
  const wasDrawerOpenRef = React.useRef(false);
  const [isDateRangeEditing, setIsDateRangeEditing] = React.useState(false);
  const hasAvailableDateBounds = Boolean(getMonthYearFromValue(availableDates));

  const lastOptionsRequestKeyRef = React.useRef('');
  const pendingCanceledRetryRef = React.useRef(null);
  const [optionsRetryTick, setOptionsRetryTick] = React.useState(0);
  const optionsFetchTimeoutRef = React.useRef(null);
  const suppressNextOptionsFetchRef = React.useRef(false);
  const clearAllRequestKeyRef = React.useRef(null);
  const resetDivisionPlanRequestKeyRef = React.useRef(null);
  const lastHandledOptionsSuccessKeyRef = React.useRef(null);
  const openCycleDatePayloadRef = React.useRef(null);
  const currentDateValuesRef = React.useRef({
    currentPeriodFromValue: defaultAppliedFilterValues.currentPeriodFrom,
    currentPeriodToValue: defaultAppliedFilterValues.currentPeriodTo,
    priorPeriodFromValue: defaultAppliedFilterValues.priorPeriodFrom,
    priorPeriodToValue: defaultAppliedFilterValues.priorPeriodTo
  });
  const currentDatePayloadRef = React.useRef(
    buildOptionsDatePayload({
      currentPeriodFromValue: defaultAppliedFilterValues.currentPeriodFrom,
      currentPeriodToValue: defaultAppliedFilterValues.currentPeriodTo,
      priorPeriodFromValue: defaultAppliedFilterValues.priorPeriodFrom,
      priorPeriodToValue: defaultAppliedFilterValues.priorPeriodTo
    })
  );
  const shouldCompareHydratedDatesRef = React.useRef(false);
  const latestCohortRequestIdRef = React.useRef(0);
  const pendingCohortLuidRef = React.useRef(null);
  const shouldRefreshCohortPersonIdsRef = React.useRef(false);
  const latestPreviewRequestIdRef = React.useRef(0);
  const pendingUnfilteredOptionsRequestKeyRef = React.useRef(null);
  const optionAvailabilityModeRef = React.useRef(null);
  const lastHydratedOptionsKeyRef = React.useRef(null);
  const lastHydratedBaselineKeyRef = React.useRef(null);
  const latestPersonIdValidationRequestIdRef = React.useRef(0);
  const lastPersonIdValidationRequestKeyRef = React.useRef(null);
  const lastPersonIdValidationResultRef = React.useRef(null);
  const cohortDraftSelectionOverrideRef = React.useRef(null);
  const cohortDraftForceSelectionRef = React.useRef(false);
  const preferCohortDraftSelectionRef = React.useRef(false);
  const restoreBaseFiltersRef = React.useRef(null);
  const freezeDivisionPlanHydrationRef = React.useRef(false);
  const hasDateDraftRef = React.useRef(false);
  const shouldApplyHydratedDatesRef = React.useRef(false);
  const dateOptionErrorRef = React.useRef(null);
  const ignoredTimeWindowDatesRef = React.useRef(null);
  const availableDateBoundsRequestIdRef = React.useRef(0);
  const hasRestoredCohortDraftRef = React.useRef(false);
  const publishedCohortBaselineRef = React.useRef(null);
  const nextCohortReturnFocusRequestKeyRef = React.useRef(0);
  const requestCohortBuilderReturnFocus = React.useCallback(() => {
    nextCohortReturnFocusRequestKeyRef.current += 1;
    setFocusCohortSelectionRequestKey(nextCohortReturnFocusRequestKeyRef.current);
  }, []);
  const createCohortBuilderDraft = React.useCallback(() => ({
    source: 'cohort-builder',
    tenantCd: tenantValue?.code || null,
    hasTenantDraftSelection,
    divisionValue: cloneChecklistOptions(divisionValue, divisionOptions),
    planValue: cloneChecklistOptions(planValue, planOptions),
    didClearAll,
    dateBasisValue,
    currentPeriodFromValue,
    currentPeriodToValue,
    personIdValue,
    priorPeriodFromValue,
    priorPeriodToValue,
    enablePriorComparison,
    timeWindowValue,
    fieldErrors: { ...(fieldErrors || {}) },
    personIdValidationMessage,
    isDivisionTouched,
    isPlanTouched,
    cascadeMode,
    isDateRangeEditing,
    cohortValue,
    populationMode,
    previewedSelection: previewedSelection
      ? {
        divisionLabels: Array.isArray(previewedSelection.divisionLabels) ? [...previewedSelection.divisionLabels] : [],
        planLabels: Array.isArray(previewedSelection.planLabels) ? [...previewedSelection.planLabels] : [],
      }
      : null,
    optionsBaselineCache: { ...(optionsBaselineCache || {}) },
    optionAvailabilityMode: optionAvailabilityModeRef.current,
    hasDateDraft: hasDateDraftRef.current,
    // Only capture a baseline when the store holds this tenant's cohorts;
    // otherwise a stale list from another tenant would misidentify the publish.
    cohortListBaseline: cohortOptionsTenantCd && cohortOptionsTenantCd === effectiveTenantValue?.code
      ? buildCohortListBaseline(cohortPersonalRaw, cohortPublicRaw)
      : null,
  }), [
    tenantValue?.code,
    hasTenantDraftSelection,
    divisionValue,
    planValue,
    didClearAll,
    dateBasisValue,
    currentPeriodFromValue,
    currentPeriodToValue,
    personIdValue,
    priorPeriodFromValue,
    priorPeriodToValue,
    enablePriorComparison,
    timeWindowValue,
    fieldErrors,
    personIdValidationMessage,
    isDivisionTouched,
    isPlanTouched,
    cascadeMode,
    isDateRangeEditing,
    cohortValue,
    populationMode,
    previewedSelection,
    optionsBaselineCache,
    cohortOptionsTenantCd,
    effectiveTenantValue?.code,
    cohortPersonalRaw,
    cohortPublicRaw,
  ]);

  const handleOpenCohortBuilder = React.useCallback(() => {
    const nextFlowId = cohortBuilderFlowId || createCohortBuilderFlowId();
    cacheCohortBuilderDrawerDraft(nextFlowId, createCohortBuilderDraft());
    return nextFlowId;
  }, [cohortBuilderFlowId, createCohortBuilderDraft]);

  const isPredefinedTimeWindow = timeWindowValue !== 'CUSTOM';
  const availableDateBoundsContextKey = `${dateBasisValue || ''}::${effectiveTenantValue?.code || ''}`;
  const canRequestAvailableDateBounds = Boolean(
    open
    && areTenantFiltersEnabled
    && isPredefinedTimeWindow
    && effectiveTenantValue?.code
  );
  const shouldRequestAvailableDateBounds = canRequestAvailableDateBounds && isDrawerInitialized;
  const isAvailableDateBoundsPending = canRequestAvailableDateBounds
    && (availableDateBoundsRequestStatus.contextKey !== availableDateBoundsContextKey
      || availableDateBoundsRequestStatus.isLoading);
  const restorablePredefinedDates = React.useMemo(
    () => (isPredefinedTimeWindow
      ? calculateTimeWindowDates(timeWindowValue, availableDates, planStartDate, planYearDateRanges, oldestAvailableDate)
      : null),
    [availableDates, isPredefinedTimeWindow, oldestAvailableDate, planStartDate, planYearDateRanges, timeWindowValue]
  );
  const parsedOldestAvailableDate = getMonthYearFromValue(oldestAvailableDate);
  const parsedCurrentPeriodFrom = getMonthYearFromValue(currentPeriodFromValue);
  const hasPriorPeriodData = priorPeriodFromValue !== PRIOR_COMPARISON_DISABLED_DATE
    && priorPeriodToValue !== PRIOR_COMPARISON_DISABLED_DATE
    && isMonthYearRangeWithinAvailableData(
      getMonthYearFromValue(priorPeriodFromValue),
      getMonthYearFromValue(priorPeriodToValue),
      parsedOldestAvailableDate,
      getMonthYearFromValue(availableDates)
    );
  const hasDisabledPriorPeriodSentinel = priorPeriodFromValue === PRIOR_COMPARISON_DISABLED_DATE
    && priorPeriodToValue === PRIOR_COMPARISON_DISABLED_DATE;
  const hasRestorablePriorPeriod = Boolean(
    hasDisabledPriorPeriodSentinel
    && restorablePredefinedDates?.priorPeriodFrom
    && restorablePredefinedDates?.priorPeriodTo
  );
  const hasAvailableDataBeforeCurrentPeriod = Boolean(
    !parsedOldestAvailableDate
    || !parsedCurrentPeriodFrom
    || getMonthYearSortValue(parsedOldestAvailableDate) < getMonthYearSortValue(parsedCurrentPeriodFrom)
  );
  const isPriorComparisonAvailable = isPredefinedTimeWindow
    ? hasPriorPeriodData || hasRestorablePriorPeriod
    : hasAvailableDataBeforeCurrentPeriod;
  const isPriorComparisonActive = isPriorComparisonAvailable && enablePriorComparison;
  const availableHistoryMonthCount = React.useMemo(
    () => getAvailableHistoryMonthCount(availableDates, oldestAvailableDate),
    [availableDates, oldestAvailableDate]
  );
  const isRolling12Available = isRolling12AvailableForHistory(availableHistoryMonthCount);
  const availableTimeWindowOptions = React.useMemo(() => timeWindowOptions.filter((option) => {
    if (option.value === 'R12') {
      return effectiveTenantValue?.rolling12Enabled !== false && isRolling12Available;
    }
    if (option.value === 'PYTD_VS_PPYTD') {
      return effectiveTenantValue?.planYTDVsPriorPlanYTDEnabled !== false;
    }
    if (option.value === 'PYTD_VS_PPY') {
      return effectiveTenantValue?.planYTDVsPriorPlanYearEnabled !== false;
    }
    if (option.value === 'CUSTOM') {
      return effectiveTenantValue?.customRangeEnabled !== false;
    }
    return true;
  }), [
    effectiveTenantValue?.customRangeEnabled,
    effectiveTenantValue?.planYTDVsPriorPlanYearEnabled,
    effectiveTenantValue?.planYTDVsPriorPlanYTDEnabled,
    effectiveTenantValue?.rolling12Enabled,
    isRolling12Available,
  ]);
  const rolling12DateRangeValues = timeWindowValue === 'R12'
    ? normalizeRolling12DateRangeValues({
      currentPeriodFromValue,
      currentPeriodToValue,
      priorPeriodFromValue,
      priorPeriodToValue,
    }, oldestAvailableDate)
    : null;
  const rolling12CurrentPeriodMonthCount = rolling12DateRangeValues
    ? getMonthRangeCount(rolling12DateRangeValues.currentPeriodFromValue, rolling12DateRangeValues.currentPeriodToValue)
    : 0;
  const rolling12PriorPeriodMonthCount = timeWindowValue === 'R12'
    ? getMonthRangeCount(rolling12DateRangeValues?.priorPeriodFromValue, rolling12DateRangeValues?.priorPeriodToValue)
    : 12;
  const hasShortRolling12PriorPeriod = timeWindowValue === 'R12'
    && rolling12CurrentPeriodMonthCount === 12
    && rolling12PriorPeriodMonthCount < 12;
  const shouldShowRolling12PriorPeriodWarning = hasShortRolling12PriorPeriod
    && rolling12PriorPeriodMonthCount > 0
    && isPriorComparisonActive
    && (availableHistoryMonthCount === null || availableHistoryMonthCount >= 12);
  const allowEmptyRolling12PriorPeriod = hasShortRolling12PriorPeriod && rolling12PriorPeriodMonthCount === 0;
  const allowEmptyPlanYtdPriorPeriod = isPredefinedTimeWindow
    && isPlanYtdTimeWindow(timeWindowValue)
    && !String(priorPeriodFromValue || '').trim()
    && !String(priorPeriodToValue || '').trim();
  const allowEmptyPriorPeriod = !isPriorComparisonActive || allowEmptyRolling12PriorPeriod || allowEmptyPlanYtdPriorPeriod;

  const clearPersonIdValidationState = React.useCallback(({ preserveMessage = false } = {}) => {
    latestPersonIdValidationRequestIdRef.current += 1;
    lastPersonIdValidationRequestKeyRef.current = null;
    lastPersonIdValidationResultRef.current = null;
    setIsPersonIdValidationLoading(false);
    if (!preserveMessage) {
      setPersonIdValidationMessage('');
    }
  }, []);

  // Clears the cohort selection and any derived person ids. Also cancels any
  // in-flight cohort person-id fetch via the request-id ref so a late response
  // cannot repopulate a cohort we just cleared.
  const clearCohortSelection = React.useCallback(() => {
    latestCohortRequestIdRef.current += 1;
    pendingCohortLuidRef.current = null;
    shouldRefreshCohortPersonIdsRef.current = false;
    setIsCohortPersonIdsLoading(false);
    setCohortValue('');
    setPopulationMode(null);
    setPersonIdValue('');
    clearPersonIdValidationState();
    setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
  }, [clearPersonIdValidationState]);

  const getSelectedLabels = React.useCallback((options) =>
    options
      .slice(1)
      .filter((option) => option.checked)
      .map((option) => option.label), []);

  const getEffectiveDateRangeValues = React.useCallback((dateValueOverrides = {}) => {
    const dateRangeValues = {
      currentPeriodFromValue,
      currentPeriodToValue,
      priorPeriodFromValue,
      priorPeriodToValue,
      ...dateValueOverrides,
    };

    const normalizedDateRangeValues = timeWindowValue === 'R12'
      ? normalizeRolling12DateRangeValues(dateRangeValues, oldestAvailableDate)
      : dateRangeValues;

    return isPriorComparisonActive
      ? normalizedDateRangeValues
      : {
        ...normalizedDateRangeValues,
        priorPeriodFromValue: PRIOR_COMPARISON_DISABLED_DATE,
        priorPeriodToValue: PRIOR_COMPARISON_DISABLED_DATE,
      };
  }, [currentPeriodFromValue, currentPeriodToValue, isPriorComparisonActive, oldestAvailableDate, priorPeriodFromValue, priorPeriodToValue, timeWindowValue]);

  const buildPersonIdValidationPayload = React.useCallback(() => {
    const tenantCd = effectiveTenantValue?.code;
    const effectiveDateRangeValues = getEffectiveDateRangeValues();
    const currentStart = monthYearToMonthStart(effectiveDateRangeValues.currentPeriodFromValue);
    const currentEnd = monthYearToMonthStart(effectiveDateRangeValues.currentPeriodToValue);
    const priorStart = monthYearToMonthStart(effectiveDateRangeValues.priorPeriodFromValue);
    const priorEnd = monthYearToMonthStart(effectiveDateRangeValues.priorPeriodToValue);

    if (!tenantCd || !currentStart || !currentEnd) {
      return null;
    }

    const divisionLabels = getSelectedLabels(divisionValue);
    const planLabels = getSelectedLabels(planValue);

    return {
      tenantCd,
      dateBasis: dateBasisValue,
      currentStart,
      currentEnd,
      ...(priorStart ? { priorStart } : {}),
      ...(priorEnd ? { priorEnd } : {}),
      ...(divisionLabels.length > 0 ? { division: divisionLabels } : {}),
      ...(planLabels.length > 0 ? { plan: planLabels } : {}),
    };
  }, [
    divisionValue,
    planValue,
    effectiveTenantValue?.code,
    dateBasisValue,
    getSelectedLabels,
    getEffectiveDateRangeValues,
  ]);

  const validatePersonIdsAgainstContext = React.useCallback(async ({ shouldSanitize = true, personIdInput = personIdValue } = {}) => {
    const normalizedPersonIdInput = String(personIdInput || '');
    const trimmedPersonIdInput = normalizedPersonIdInput.trim();

    if (!trimmedPersonIdInput) {
      clearPersonIdValidationState();
      setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      return {
        ok: true,
        sanitizedPersonIds: '',
      };
    }

    if (normalizedPersonIdInput.length > PERSON_ID_MAX_LENGTH) {
      setPersonIdValidationMessage('');
      setFieldErrors((currentValue) => ({ ...currentValue, personId: PERSON_ID_CHARACTER_LIMIT_ERROR }));
      return { ok: false };
    }

    if (!isValidPersonIdValue(normalizedPersonIdInput)) {
      setPersonIdValidationMessage('');
      setFieldErrors((currentValue) => ({ ...currentValue, personId: PERSON_ID_FORMAT_ERROR }));
      return { ok: false };
    }

    const payload = buildPersonIdValidationPayload();
    if (!payload) {
      setPersonIdValidationMessage('');
      setFieldErrors((currentValue) => ({ ...currentValue, personId: PERSON_ID_VALIDATION_ERROR }));
      return { ok: false };
    }

    const normalizedPersonIds = normalizePersonIdValues(normalizedPersonIdInput);
    const totalEnteredCount = normalizedPersonIds.length;
    const uniquePersonIds = getUniquePersonIdValues(normalizedPersonIds);
    if (uniquePersonIds.length === 0) {
      clearPersonIdValidationState();
      setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      return {
        ok: true,
        sanitizedPersonIds: '',
      };
    }

    const requestKey = JSON.stringify({
      ...payload,
      personIds: normalizedPersonIds,
    });

    if (requestKey === lastPersonIdValidationRequestKeyRef.current && lastPersonIdValidationResultRef.current) {
      const cachedResult = lastPersonIdValidationResultRef.current;

      if (shouldSanitize && cachedResult.sanitizedPersonIds !== normalizedPersonIdInput) {
        setPersonIdValue(cachedResult.sanitizedPersonIds);
      }

      setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      setPersonIdValidationMessage(cachedResult.validationMessage);
      return {
        ok: true,
        sanitizedPersonIds: cachedResult.sanitizedPersonIds,
      };
    }

    const requestId = latestPersonIdValidationRequestIdRef.current + 1;
    latestPersonIdValidationRequestIdRef.current = requestId;
    setIsPersonIdValidationLoading(true);

    try {
      const validPersonIdSet = await fetchValidPersonIds(payload);

      if (latestPersonIdValidationRequestIdRef.current !== requestId) {
        return { ok: false };
      }

      if (!(validPersonIdSet instanceof Set)) {
        setPersonIdValidationMessage('');
        setFieldErrors((currentValue) => ({ ...currentValue, personId: PERSON_ID_VALIDATION_ERROR }));
        return { ok: false };
      }

      const validUniquePersonIds = uniquePersonIds.filter((id) => validPersonIdSet.has(id));
      const duplicateCount = totalEnteredCount - uniquePersonIds.length;
      const invalidCount = uniquePersonIds.length - validUniquePersonIds.length;
      const excludedCount = duplicateCount + invalidCount;
      const appliedCount = validUniquePersonIds.length;
      const sanitizedPersonIds = validUniquePersonIds.join(',');
      const validationMessage = excludedCount > 0
        ? appliedCount === 0
          ? `0 of ${totalEnteredCount} Person IDs will be applied. No valid Person IDs were found.`
          : `${appliedCount} of ${totalEnteredCount} Person IDs will be applied. ${excludedCount} ${excludedCount === 1 ? 'entry was' : 'entries were'} excluded because ${excludedCount === 1 ? 'it is' : 'they are'} invalid or ${excludedCount === 1 ? 'a duplicate' : 'duplicates'}.`
        : `${appliedCount} of ${totalEnteredCount} Person IDs will be applied`;

      if (shouldSanitize && sanitizedPersonIds !== normalizedPersonIdInput) {
        setPersonIdValue(sanitizedPersonIds);
      }

      setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      setPersonIdValidationMessage(validationMessage);

      lastPersonIdValidationRequestKeyRef.current = requestKey;
      lastPersonIdValidationResultRef.current = {
        sanitizedPersonIds,
        validationMessage,
      };

      return {
        ok: true,
        sanitizedPersonIds,
      };
    } catch (error) {
      if (latestPersonIdValidationRequestIdRef.current === requestId) {
        setPersonIdValidationMessage('');
        setFieldErrors((currentValue) => ({ ...currentValue, personId: PERSON_ID_VALIDATION_ERROR }));
      }
      return { ok: false };
    } finally {
      if (latestPersonIdValidationRequestIdRef.current === requestId) {
        setIsPersonIdValidationLoading(false);
      }
    }
  }, [
    personIdValue,
    clearPersonIdValidationState,
    buildPersonIdValidationPayload,
  ]);

const personIdValidationContextKey = React.useMemo(() => JSON.stringify(buildPersonIdValidationPayload()), [buildPersonIdValidationPayload]);
  const validatePersonIdsAgainstContextRef = React.useRef(validatePersonIdsAgainstContext);

  React.useEffect(() => {
    validatePersonIdsAgainstContextRef.current = validatePersonIdsAgainstContext;
  }, [validatePersonIdsAgainstContext]);

  React.useEffect(() => {
    const hasPersonIdsToRevalidate = Boolean(
      open
      && (populationMode === POPULATION_MODE.PERSON_ID || populationMode === POPULATION_MODE.COHORT)
      && String(personIdValue || '').trim()
    );
    const hasValidValidationContext = personIdValidationContextKey !== 'null';
    const shouldRevalidatePersonIds = hasPersonIdsToRevalidate
      && !isDateRangeEditing
      && hasValidValidationContext;

    clearPersonIdValidationState({
      preserveMessage: hasPersonIdsToRevalidate && (isDateRangeEditing || hasValidValidationContext),
    });

    if (shouldRevalidatePersonIds) {
      Promise.resolve(validatePersonIdsAgainstContextRef.current())
        .catch(() => {
          // Validation errors are already reflected in component state.
        });
    }
  }, [
    open,
    isDateRangeEditing,
    populationMode,
    personIdValidationContextKey,
    clearPersonIdValidationState,
  ]);

  const dateMonthYearOptions = React.useMemo(
    () => buildMonthYearOptions(availableDates, oldestAvailableDate),
    [availableDates, oldestAvailableDate]
  );
  const predefinedCurrentPeriodDateRanges = React.useMemo(
    () => {
      let currentPeriodFromOptionValues = [];

      if (timeWindowValue === 'R12') {
        currentPeriodFromOptionValues = getMonthYearOptionsThrough(
          dateMonthYearOptions,
          getMonthYearValueWithOffset(availableDates, -11)
        );
      } else if (isPlanYtdTimeWindow(timeWindowValue)) {
        currentPeriodFromOptionValues = getPlanYearStartMonthOptions(
          dateMonthYearOptions,
          planYearDateRanges,
          oldestAvailableDate
        );
      }

      // Calculate each To option from the previously valid start anchors so changing
      // which field is editable preserves the exact set of available date windows.
      const seenCurrentPeriodToValues = new Set();
      return currentPeriodFromOptionValues
        .map((currentPeriodFromOptionValue) => (
          timeWindowValue === 'R12'
            ? calculateRolling12DatesFromCurrentStart(currentPeriodFromOptionValue, oldestAvailableDate)
            : calculatePlanYtdDatesFromCurrentStart(
              currentPeriodFromOptionValue,
              timeWindowValue,
              availableDates,
              planYearDateRanges,
              oldestAvailableDate
            )
        ))
        .filter((dateRange) => {
          if (!dateRange?.currentPeriodTo || seenCurrentPeriodToValues.has(dateRange.currentPeriodTo)) {
            return false;
          }

          seenCurrentPeriodToValues.add(dateRange.currentPeriodTo);
          return true;
        });
    },
    [availableDates, dateMonthYearOptions, oldestAvailableDate, planYearDateRanges, timeWindowValue]
  );
  const currentPeriodToOptions = React.useMemo(
    () => (isPredefinedTimeWindow
      ? predefinedCurrentPeriodDateRanges.map((dateRange) => dateRange.currentPeriodTo)
      : getToMonthYearOptions(dateMonthYearOptions, currentPeriodFromValue)),
    [currentPeriodFromValue, dateMonthYearOptions, isPredefinedTimeWindow, predefinedCurrentPeriodDateRanges]
  );
  const currentPeriodFromOptions = React.useMemo(
    () => {
      if (isPredefinedTimeWindow) {
        return [];
      }

      const optionsThroughCurrentTo = getFromMonthYearOptions(dateMonthYearOptions, currentPeriodToValue);
      return isPriorComparisonActive
        ? getAfterMonthYearOptions(optionsThroughCurrentTo, priorPeriodToValue)
        : optionsThroughCurrentTo;
    },
    [currentPeriodToValue, dateMonthYearOptions, isPredefinedTimeWindow, isPriorComparisonActive, priorPeriodToValue]
  );
  const hasCurrentPeriodToOptions = currentPeriodToOptions.length > 0;
  const hasSelectedCurrentPeriodToOption = currentPeriodToOptions.includes(currentPeriodToValue);
  // A nonempty dropdown without a matching selection is incomplete input, not
  // evidence that the selected window lacks prior-period data.
  const shouldShowPriorComparisonUnavailable = Boolean(
    open
    && isDrawerInitialized
    && !isSavedFilterPreviewLoading
    && !isPriorComparisonAvailable
    && !isAvailableDateBoundsPending
    && hasAvailableDateBounds
    && (!hasCurrentPeriodToOptions || hasSelectedCurrentPeriodToOption)
  );
  const priorPeriodToOptions = React.useMemo(
    () => getBeforeMonthYearOptions(getToMonthYearOptions(dateMonthYearOptions, priorPeriodFromValue), currentPeriodFromValue),
    [dateMonthYearOptions, priorPeriodFromValue, currentPeriodFromValue]
  );
  const priorPeriodFromOptions = React.useMemo(
    () => getBeforeMonthYearOptions(getFromMonthYearOptions(dateMonthYearOptions, priorPeriodToValue), currentPeriodFromValue),
    [dateMonthYearOptions, priorPeriodToValue, currentPeriodFromValue]
  );
  const dateFieldOptionsByName = React.useMemo(() => ({
    currentPeriodFromValue: currentPeriodFromOptions,
    currentPeriodToValue: currentPeriodToOptions,
    priorPeriodFromValue: priorPeriodFromOptions,
    priorPeriodToValue: priorPeriodToOptions,
  }), [currentPeriodFromOptions, currentPeriodToOptions, priorPeriodFromOptions, priorPeriodToOptions]);

  const setDateRangeValues = React.useCallback((nextValues) => {
    setCurrentPeriodFromValue(nextValues.currentPeriodFromValue);
    setCurrentPeriodToValue(nextValues.currentPeriodToValue);
    setPriorPeriodFromValue(nextValues.priorPeriodFromValue);
    setPriorPeriodToValue(nextValues.priorPeriodToValue);
  }, []);

  React.useEffect(() => {
    if (timeWindowValue !== 'R12' || isDateRangeEditing || !isPriorComparisonActive) {
      return;
    }

    // For R12 the Current Period To field is the only user-editable date. If the user
    // has typed an invalid or out-of-options value, skip normalization so the error
    // state is preserved and the invalid value is not silently overwritten.
    if (dateOptionErrorRef.current || !isValidMonthValue(String(currentPeriodToValue || ''))) {
      return;
    }

    // For R12 the Current Period To field is the only user-editable date. If the user
    // has typed an invalid or out-of-options value, skip normalization so the error
    // state is preserved and the invalid value is not silently overwritten.
    if (dateOptionErrorRef.current || !isValidMonthValue(String(currentPeriodToValue || ''))) {
      return;
    }

    const effectiveDateRangeValues = getEffectiveDateRangeValues();
    const currentDateRangeValues = {
      currentPeriodFromValue,
      currentPeriodToValue,
      priorPeriodFromValue,
      priorPeriodToValue,
    };

    if (JSON.stringify(effectiveDateRangeValues) !== JSON.stringify(currentDateRangeValues)) {
      setDateRangeValues(effectiveDateRangeValues);
    }
  }, [currentPeriodFromValue, currentPeriodToValue, getEffectiveDateRangeValues, isDateRangeEditing, isPriorComparisonActive, priorPeriodFromValue, priorPeriodToValue, setDateRangeValues, timeWindowValue]);

  React.useEffect(() => {
    currentDateValuesRef.current = {
      currentPeriodFromValue,
      currentPeriodToValue,
      priorPeriodFromValue,
      priorPeriodToValue
    };

    currentDatePayloadRef.current = buildOptionsDatePayload(getEffectiveDateRangeValues());
  }, [currentPeriodFromValue, currentPeriodToValue, getEffectiveDateRangeValues, priorPeriodFromValue, priorPeriodToValue]);

  React.useEffect(() => {
    if (!timeWindowDates || timeWindowDates === ignoredTimeWindowDatesRef.current) {
      return;
    }

    if (timeWindowDatesTimeWindow && timeWindowDatesTimeWindow !== timeWindowValue) {
      return;
    }

    ignoredTimeWindowDatesRef.current = null;

    if (timeWindowValue === 'CUSTOM') {
      shouldCompareHydratedDatesRef.current = false;
      shouldApplyHydratedDatesRef.current = false;
      return;
    }

    const currentDateValues = currentDateValuesRef.current;
    const shouldApplyHydratedDates = shouldApplyHydratedDatesRef.current;
    const normalizedTimeWindowDates = timeWindowValue === 'R12'
      ? shouldApplyHydratedDates || !currentDateValues.currentPeriodFromValue
        ? normalizeRolling12DatesForAvailableHistory(timeWindowDates, oldestAvailableDate)
        : calculateRolling12DatesFromCurrentStart(currentDateValues.currentPeriodFromValue, oldestAvailableDate) || normalizeRolling12DatesForAvailableHistory(timeWindowDates, oldestAvailableDate)
      : timeWindowDates;
    const isDateDraftActive = hasDateDraftRef.current && hasAnyDateRangeValue(currentDateValues);

    if (isDateDraftActive) {
      shouldCompareHydratedDatesRef.current = false;
      shouldApplyHydratedDatesRef.current = false;
      return;
    }

    const nextCurrentPeriodFromValue = shouldApplyHydratedDates
      ? normalizedTimeWindowDates.currentPeriodFrom ?? currentDateValues.currentPeriodFromValue
      : currentDateValues.currentPeriodFromValue || normalizedTimeWindowDates.currentPeriodFrom;
    const nextCurrentPeriodToValue = shouldApplyHydratedDates
      ? normalizedTimeWindowDates.currentPeriodTo ?? currentDateValues.currentPeriodToValue
      : currentDateValues.currentPeriodToValue || normalizedTimeWindowDates.currentPeriodTo;
    const nextPriorPeriodFromValue = shouldApplyHydratedDates
      ? normalizedTimeWindowDates.priorPeriodFrom ?? currentDateValues.priorPeriodFromValue
      : currentDateValues.priorPeriodFromValue || normalizedTimeWindowDates.priorPeriodFrom;
    const nextPriorPeriodToValue = shouldApplyHydratedDates
      ? normalizedTimeWindowDates.priorPeriodTo ?? currentDateValues.priorPeriodToValue
      : currentDateValues.priorPeriodToValue || normalizedTimeWindowDates.priorPeriodTo;

    // AGENT-CONTEXT: The hydrated /options payload MUST mirror the date values that
    // are actually committed to state (the next*Value vars), NOT the raw recomputed
    // named-window defaults. For a Plan YTD window, `normalizedTimeWindowDates`
    // always carries the DEFAULT plan-year start (calculateTimeWindowDates recomputes
    // from planStartDate and ignores a saved non-default "current period from"). When
    // shouldApplyHydratedDates is false (drawer reopen with a saved filter), the saved
    // dates are preserved in state, so the options fetch must use them too. Building
    // this payload from normalizedTimeWindowDates instead raced the main options
    // effect and fired a /options request with default dates, causing the UI to show
    // no divisions/plans. Override only if the options fetch is intentionally
    // decoupled from the committed date-range state.
    const hydratedDatePayload = buildOptionsDatePayload(getEffectiveDateRangeValues({
      currentPeriodFromValue: nextCurrentPeriodFromValue,
      currentPeriodToValue: nextCurrentPeriodToValue,
      priorPeriodFromValue: nextPriorPeriodFromValue,
      priorPeriodToValue: nextPriorPeriodToValue
    }));

    if (open && shouldCompareHydratedDatesRef.current) {
      const previousDatePayload = openCycleDatePayloadRef.current || {};
      const hasHydratedDateChanges = JSON.stringify(hydratedDatePayload) !== JSON.stringify(previousDatePayload);

      if (hasHydratedDateChanges && effectiveTenantValue?.code && areTenantFiltersEnabled && !isDateRangeEditing) {
        const selectedDivisionLabels = divisionValue
          .slice(1)
          .filter((option) => option.checked)
          .map((option) => option.label);
        const selectedPlanLabels = planValue
          .slice(1)
          .filter((option) => option.checked)
          .map((option) => option.label);
        const hasDivisionSelection = selectedDivisionLabels.length > 0;
        const hasPlanSelection = selectedPlanLabels.length > 0;
        const hydratedOptionsPayload = {
          tenantCd: effectiveTenantValue.code,
          dateBasis: dateBasisValue,
          ...hydratedDatePayload,
          ...(cascadeMode === 'division' && hasDivisionSelection ? { division: selectedDivisionLabels } : {}),
          ...(cascadeMode === 'plan' && hasPlanSelection ? { plan: selectedPlanLabels } : {})
        };
        const hydratedOptionsRequestKey = JSON.stringify(hydratedOptionsPayload);

        if (
          hydratedOptionsRequestKey !== lastOptionsRequestKeyRef.current
          && hydratedOptionsRequestKey !== globalFilterOptionsRequestKey
          && hydratedOptionsRequestKey !== globalFilterOptionsLastSuccessKey
        ) {
          if (optionsFetchTimeoutRef.current) {
            window.clearTimeout(optionsFetchTimeoutRef.current);
            optionsFetchTimeoutRef.current = null;
          }
          lastOptionsRequestKeyRef.current = hydratedOptionsRequestKey;
          dispatch(fetchGlobalFilterOptions({ requestKey: hydratedOptionsRequestKey, payload: hydratedOptionsPayload }));
        }
      }

      shouldCompareHydratedDatesRef.current = false;
    }

    if (shouldApplyHydratedDates) {
      setCurrentPeriodFromValue(nextCurrentPeriodFromValue || '');
    } else if (!currentDateValues.currentPeriodFromValue) {
      if (nextCurrentPeriodFromValue) setCurrentPeriodFromValue(nextCurrentPeriodFromValue);
    }
    if (shouldApplyHydratedDates) {
      setCurrentPeriodToValue(nextCurrentPeriodToValue || '');
    } else if (!currentDateValues.currentPeriodToValue) {
      if (nextCurrentPeriodToValue) setCurrentPeriodToValue(nextCurrentPeriodToValue);
    }
    if (shouldApplyHydratedDates) {
      setPriorPeriodFromValue(nextPriorPeriodFromValue || '');
    } else if (!currentDateValues.priorPeriodFromValue) {
      if (nextPriorPeriodFromValue) setPriorPeriodFromValue(nextPriorPeriodFromValue);
    }
    if (shouldApplyHydratedDates) {
      setPriorPeriodToValue(nextPriorPeriodToValue || '');
    } else if (!currentDateValues.priorPeriodToValue) {
      if (nextPriorPeriodToValue) setPriorPeriodToValue(nextPriorPeriodToValue);
    }
    shouldApplyHydratedDatesRef.current = false;
    setFieldErrors((prev) => {
      if (!prev.dateRange && !prev.dateRangeFields) return prev;
      const { dateRange, dateRangeFields, ...rest } = prev;
      return rest;
    });
  }, [
    timeWindowDates,
    timeWindowDatesTimeWindow,
    open,
    timeWindowValue,
    divisionValue,
    planValue,
    cascadeMode,
    effectiveTenantValue?.code,
    areTenantFiltersEnabled,
    isDateRangeEditing,
    dateBasisValue,
    globalFilterOptionsRequestKey,
    globalFilterOptionsLastSuccessKey,
    oldestAvailableDate,
    getEffectiveDateRangeValues,
    dispatch
  ]);

  // Wait until drawer state has been initialized for the current open cycle
  React.useEffect(() => {
    if (!open) {
      setIsDrawerInitialized(false);
      return;
    }

    setIsDrawerInitialized(true);
  }, [open]);

  // Fetch available dates after drawer state has been initialized
  React.useEffect(() => {
    const effectiveTenantCode = effectiveTenantValue?.code;

    if (!shouldRequestAvailableDateBounds || !effectiveTenantCode) {
      return;
    }

    openCycleDatePayloadRef.current = currentDatePayloadRef.current;
    shouldCompareHydratedDatesRef.current = true;

    const requestId = availableDateBoundsRequestIdRef.current + 1;
    availableDateBoundsRequestIdRef.current = requestId;
    setAvailableDateBoundsRequestStatus({
      contextKey: availableDateBoundsContextKey,
      isLoading: true,
    });

    Promise.resolve(dispatch(fetchAvailableDateBoundsAndPlanYear(
      dateBasisValue,
      timeWindowValue,
      effectiveTenantCode,
      { hideSpinner: true }
    )))
      .catch(() => undefined)
      .finally(() => {
        if (availableDateBoundsRequestIdRef.current === requestId) {
          setAvailableDateBoundsRequestStatus({
            contextKey: availableDateBoundsContextKey,
            isLoading: false,
          });
        }
      });

    return () => {
      if (availableDateBoundsRequestIdRef.current === requestId) {
        availableDateBoundsRequestIdRef.current += 1;
      }
    };
  }, [availableDateBoundsContextKey, dateBasisValue, dispatch, effectiveTenantValue?.code, shouldRequestAvailableDateBounds, timeWindowValue]);

  React.useEffect(() => {
    if (!open) {

      wasDrawerOpenRef.current = false;
      setDidClearAll(false);

      setIsDivisionTouched(false);
      setIsPlanTouched(false);
      setCascadeMode(null);
      setDivisionValue(divisionOptions.map((option) => ({ ...option })));
      setPlanValue(planOptions.map((option) => ({ ...option })));
      setDivisionSearchQuery('');
      setPlanSearchQuery('');
      setFieldErrors((currentValue) => ({ ...currentValue, division: null, plan: null }));
      // Reset transient refs/state that can prevent /options from being fetched
      // when the drawer is reopened (e.g., after closing while editing dates).
      lastOptionsRequestKeyRef.current = '';
      suppressNextOptionsFetchRef.current = false;
      clearAllRequestKeyRef.current = null;
      resetDivisionPlanRequestKeyRef.current = null;
      lastHandledOptionsSuccessKeyRef.current = null;
      openCycleDatePayloadRef.current = null;
      currentDatePayloadRef.current = buildOptionsDatePayload({
        currentPeriodFromValue: defaultAppliedFilterValues.currentPeriodFrom,
        currentPeriodToValue: defaultAppliedFilterValues.currentPeriodTo,
        priorPeriodFromValue: defaultAppliedFilterValues.priorPeriodFrom,
        priorPeriodToValue: defaultAppliedFilterValues.priorPeriodTo
      });
      shouldCompareHydratedDatesRef.current = false;
      shouldApplyHydratedDatesRef.current = false;
      setIsDateRangeEditing(false);
      latestCohortRequestIdRef.current = 0;
      pendingCohortLuidRef.current = null;
      shouldRefreshCohortPersonIdsRef.current = false;
      setIsCohortPersonIdsLoading(false);
      // Reset population state to neutral so the detect-deleted-cohort effect
      // cannot fire with stale draft values on the next open before the init
      // effect has a chance to apply the correct appliedFilters values.
      setPopulationMode(null);
      setCohortValue('');
      setPersonIdValue(defaultAppliedFilterValues.personIdValue);
      // The drawer is kept mounted, so synchronize this value while hidden. Otherwise
      // the next open paints the previous draft before applied filters are restored.
      setEnablePriorComparison(appliedFilters?.enablePriorComparison !== false);
      latestPreviewRequestIdRef.current += 1;
      setIsSavedFilterPreviewLoading(false);
      setPreviewedSelection(null);
      cohortDraftSelectionOverrideRef.current = null;
      cohortDraftForceSelectionRef.current = false;
      preferCohortDraftSelectionRef.current = false;
      restoreBaseFiltersRef.current = null;
      freezeDivisionPlanHydrationRef.current = false;
      hasRestoredCohortDraftRef.current = false;
      publishedCohortBaselineRef.current = null;
      pendingUnfilteredOptionsRequestKeyRef.current = null;
      optionAvailabilityModeRef.current = null;
      lastHydratedOptionsKeyRef.current = null;
      lastHydratedBaselineKeyRef.current = null;
      clearPersonIdValidationState();

      dispatch(resetGlobalFilterOptions());
      return;
    }

    const isDrawerOpening = !wasDrawerOpenRef.current;
    wasDrawerOpenRef.current = true;

    const nextTenant = Array.isArray(companies) ? companies.find((company) => company.code === currentTenantCd) || null : null;
    const cohortReturnTenant = returnFromCohortBuilder && cohortBuilderTenantCd
      ? (Array.isArray(companies) ? companies.find((company) => company.code === cohortBuilderTenantCd) || null : null)
      : null;
    const cohortDraft = getCohortBuilderDrawerDraft(cohortBuilderFlowId);
    const hasRestorableCohortDraft = Boolean(
      cohortDraft
      && typeof cohortDraft === 'object'
      && cohortDraft.source === 'cohort-builder'
    );

    // If a draft exists but the user did not return via the Cohort Builder return
    // flow (e.g. browser-back or other navigation), discard it so it cannot be
    // applied unexpectedly on a future open.
    if (hasRestorableCohortDraft && !returnFromCohortBuilder) {
      clearCohortBuilderDrawerDraft(cohortBuilderFlowId);
    }

    const shouldRestoreCohortDraft = Boolean(
      hasRestorableCohortDraft
      && returnFromCohortBuilder
      && !hasRestoredCohortDraftRef.current
    );

    if (!isDrawerOpening && !shouldRestoreCohortDraft) {
      return;
    }

    const draftDateRangeValues = shouldRestoreCohortDraft
      ? {
        currentPeriodFromValue: cohortDraft.currentPeriodFromValue || defaultAppliedFilterValues.currentPeriodFrom,
        currentPeriodToValue: cohortDraft.currentPeriodToValue || defaultAppliedFilterValues.currentPeriodTo,
        priorPeriodFromValue: cohortDraft.priorPeriodFromValue || defaultAppliedFilterValues.priorPeriodFrom,
        priorPeriodToValue: cohortDraft.priorPeriodToValue || defaultAppliedFilterValues.priorPeriodTo,
      }
      : null;
    const draftTenant = shouldRestoreCohortDraft && Array.isArray(companies)
      ? companies.find((company) => company.code === cohortDraft.tenantCd) || null
      : null;
    const tenantForOpen = draftTenant || cohortReturnTenant || nextTenant;
    const hasTenantOverride = Boolean(cohortReturnTenant && cohortReturnTenant.code !== currentTenantCd);

    setTenantValue(tenantForOpen);
    setHasTenantDraftSelection(hasTenantOverride);
    setDidClearAll(false);

    // Always allow a fresh options fetch on open; otherwise the last request key
    // or a leftover suppression flag can cause /options to be skipped.
    lastOptionsRequestKeyRef.current = '';
    suppressNextOptionsFetchRef.current = false;
    clearAllRequestKeyRef.current = null;
    resetDivisionPlanRequestKeyRef.current = null;
    lastHandledOptionsSuccessKeyRef.current = null;
    openCycleDatePayloadRef.current = null;
    currentDatePayloadRef.current = buildOptionsDatePayload({
      currentPeriodFromValue: defaultAppliedFilterValues.currentPeriodFrom,
      currentPeriodToValue: defaultAppliedFilterValues.currentPeriodTo,
      priorPeriodFromValue: defaultAppliedFilterValues.priorPeriodFrom,
      priorPeriodToValue: defaultAppliedFilterValues.priorPeriodTo
    });
    shouldCompareHydratedDatesRef.current = false;
    shouldApplyHydratedDatesRef.current = false;
    setIsDateRangeEditing(false);
    latestCohortRequestIdRef.current = 0;
    pendingCohortLuidRef.current = null;
    shouldRefreshCohortPersonIdsRef.current = false;
    setIsCohortPersonIdsLoading(false);
    pendingUnfilteredOptionsRequestKeyRef.current = null;
    optionAvailabilityModeRef.current = null;
    lastHydratedOptionsKeyRef.current = null;
    lastHydratedBaselineKeyRef.current = null;
    clearPersonIdValidationState();

    if (shouldRestoreCohortDraft) {
      const restoredDivisionValue = cloneChecklistOptions(cohortDraft.divisionValue, divisionOptions);
      const restoredPlanValue = cloneChecklistOptions(cohortDraft.planValue, planOptions);
      const restoredPreviewedSelection = cohortDraft.previewedSelection || {
        divisionLabels: summarizeCheckedOptions(restoredDivisionValue).checked,
        planLabels: summarizeCheckedOptions(restoredPlanValue).checked,
      };
      const restoredDivisionSummary = summarizeCheckedOptions(restoredDivisionValue);
      const restoredPlanSummary = summarizeCheckedOptions(restoredPlanValue);

      setTenantValue(draftTenant || cohortReturnTenant || nextTenant);
      setHasTenantDraftSelection(Boolean(cohortDraft.hasTenantDraftSelection));
      // Clear-all/touched/cascade are transient interaction flags; restoring them can
      // block checkbox rehydration and drop checked state on Cohort return.
      setDidClearAll(false);
      setDivisionValue(restoredDivisionValue);
      setPlanValue(restoredPlanValue);
      setDateBasisValue(cohortDraft.dateBasisValue || defaultAppliedFilterValues.dateBasis);
      setDateRangeValues(draftDateRangeValues);
      currentDateValuesRef.current = draftDateRangeValues;
      currentDatePayloadRef.current = buildOptionsDatePayload(draftDateRangeValues);
      setPersonIdValue(cohortDraft.personIdValue ?? defaultAppliedFilterValues.personIdValue);
      setEnablePriorComparison(cohortDraft.enablePriorComparison !== false);
      setTimeWindowValue(cohortDraft.timeWindowValue || defaultAppliedFilterValues.timeWindow);
      setFieldErrors(cohortDraft.fieldErrors && typeof cohortDraft.fieldErrors === 'object'
        ? cohortDraft.fieldErrors
        : {});
      setPersonIdValidationMessage(cohortDraft.personIdValidationMessage || '');
      setIsDivisionTouched(false);
      setIsPlanTouched(false);
      setCascadeMode(null);
      if (cohortWasPublished) {
        const restoredPopulationMode =
          appliedFilters?.populationMode || (appliedFilters?.personIdValue ? POPULATION_MODE.PERSON_ID : null);
        setPopulationMode(restoredPopulationMode);
        setCohortValue(appliedFilters?.cohortLuid || '');
        shouldRefreshCohortPersonIdsRef.current =
          restoredPopulationMode === POPULATION_MODE.COHORT && Boolean(appliedFilters?.cohortLuid);
        publishedCohortBaselineRef.current =
          cohortDraft.cohortListBaseline && typeof cohortDraft.cohortListBaseline === 'object'
            ? cohortDraft.cohortListBaseline
            : null;
      } else {
        setPopulationMode(cohortDraft.populationMode || null);
        setCohortValue(cohortDraft.cohortValue || '');
        publishedCohortBaselineRef.current = null;
      }
      setPreviewedSelection(restoredPreviewedSelection);
      cohortDraftSelectionOverrideRef.current = restoredPreviewedSelection;
      cohortDraftForceSelectionRef.current = true;
      preferCohortDraftSelectionRef.current = true;
      restoreBaseFiltersRef.current = {
        divisionLabels: restoredDivisionSummary.checked,
        planLabels: restoredPlanSummary.checked,
        isAllDivisionsSelected: restoredDivisionSummary.isAllSelected,
        isAllPlansSelected: restoredPlanSummary.isAllSelected,
      };
      freezeDivisionPlanHydrationRef.current = true;
      setOptionsBaselineCache(
        cohortDraft.optionsBaselineCache && typeof cohortDraft.optionsBaselineCache === 'object'
          ? cohortDraft.optionsBaselineCache
          : {}
      );
      optionAvailabilityModeRef.current = null;
      hasDateDraftRef.current = Boolean(cohortDraft.hasDateDraft);
      setIsDateRangeEditing(Boolean(cohortDraft.isDateRangeEditing));

      hasRestoredCohortDraftRef.current = true;
      clearCohortBuilderDrawerDraft(cohortBuilderFlowId);
      return;
    }

    setDivisionValue(divisionOptions.map((option) => ({ ...option })));
    setPlanValue(planOptions.map((option) => ({ ...option })));
    const nextTimeWindowValue = appliedFilters?.timeWindow || defaultAppliedFilterValues.timeWindow;

    hasDateDraftRef.current = false;
    setDateBasisValue(appliedFilters?.dateBasis || defaultAppliedFilterValues.dateBasis);
    setDateRangeValues({
      currentPeriodFromValue: appliedFilters?.currentPeriodFrom || defaultAppliedFilterValues.currentPeriodFrom,
      currentPeriodToValue: appliedFilters?.currentPeriodTo || defaultAppliedFilterValues.currentPeriodTo,
      priorPeriodFromValue: appliedFilters?.priorPeriodFrom || defaultAppliedFilterValues.priorPeriodFrom,
      priorPeriodToValue: appliedFilters?.priorPeriodTo || defaultAppliedFilterValues.priorPeriodTo,
    });
    setEnablePriorComparison(appliedFilters?.enablePriorComparison !== false);
    setTimeWindowValue(nextTimeWindowValue);
    setPersonIdValue(appliedFilters?.personIdValue ?? defaultAppliedFilterValues.personIdValue);
    setFieldErrors({});
    setIsDivisionTouched(false);
    setIsPlanTouched(false);
    const restoredPopulationMode =
      appliedFilters?.populationMode || (appliedFilters?.personIdValue ? POPULATION_MODE.PERSON_ID : null);
    setPopulationMode(restoredPopulationMode);
    setCohortValue(appliedFilters?.cohortLuid || '');
    // When a cohort is restored on open, re-derive its person ids once the cohort's
    // validity is confirmed against the tenant's freshly fetched options.
    shouldRefreshCohortPersonIdsRef.current =
      restoredPopulationMode === POPULATION_MODE.COHORT && Boolean(appliedFilters?.cohortLuid);
    const hasPartialAppliedDivisionSelection =
      Array.isArray(appliedFilters?.divisionLabels)
      && appliedFilters.divisionLabels.length > 0
      && appliedFilters?.isAllDivisionsSelected === false;
    const hasPartialAppliedPlanSelection =
      Array.isArray(appliedFilters?.planLabels)
      && appliedFilters.planLabels.length > 0
      && appliedFilters?.isAllPlansSelected === false;

    if (hasPartialAppliedDivisionSelection) {
      optionAvailabilityModeRef.current = 'division';
      setCascadeMode('division');
    } else if (hasPartialAppliedPlanSelection) {
      optionAvailabilityModeRef.current = 'plan';
      setCascadeMode('plan');
    } else {
      optionAvailabilityModeRef.current = null;
      setCascadeMode(null);
    }
    cohortDraftSelectionOverrideRef.current = null;
    preferCohortDraftSelectionRef.current = false;
    restoreBaseFiltersRef.current = null;
    freezeDivisionPlanHydrationRef.current = false;
    hasRestoredCohortDraftRef.current = false;
    publishedCohortBaselineRef.current = null;
  }, [
    appliedFilters,
    cohortBuilderFlowId,
    cohortBuilderTenantCd,
    companies,
    currentTenantCd,
    open,
    returnFromCohortBuilder,
    cohortWasPublished,
    setDateRangeValues,
    clearPersonIdValidationState,
    dispatch,
  ]);

  React.useEffect(() => {
    if (!open || !returnFromCohortBuilder || cohortWasPublished) {
      return;
    }

    requestCohortBuilderReturnFocus();
    onCohortBuilderReturnConsumed?.();
  }, [open, returnFromCohortBuilder, cohortWasPublished, onCohortBuilderReturnConsumed, requestCohortBuilderReturnFocus]);

  React.useEffect(() => {
    const tenantCd = effectiveTenantValue?.code;
    if (!open || !returnFromCohortBuilder || !cohortWasPublished || !tenantCd) {
      setPublishedCohortRefreshTenantCd(null);
      return undefined;
    }

    let cancelled = false;
    let retryTimeoutId = null;
    setPublishedCohortRefreshTenantCd(null);

    // With a pre-publish baseline we can tell whether a fetched list already
    // reflects the publish; without one a single forced refresh is the best we
    // can do.
    const baseline = publishedCohortBaselineRef.current;
    const isPublishedCohortInStore = () => {
      const foState = reduxStore.getState()?.fo || {};
      return foState.cohortOptionsTenantCd === tenantCd
        && Boolean(findPublishedCohortLuid(foState.cohortPersonal, foState.cohortPublic, baseline));
    };

    const waitBeforeRetry = () => new Promise((resolve) => {
      retryTimeoutId = window.setTimeout(resolve, PUBLISHED_COHORT_REFRESH_RETRY_DELAY_MS);
    });

    const refreshPublishedCohorts = async () => {
      const maxAttempts = baseline ? PUBLISHED_COHORT_REFRESH_MAX_ATTEMPTS : 1;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        await dispatch(fetchCohortOptions(tenantCd, { forceRefresh: true }));
        if (cancelled) return;
        if (attempt === maxAttempts || isPublishedCohortInStore()) break;
        await waitBeforeRetry();
        if (cancelled) return;
      }
      setPublishedCohortRefreshTenantCd(tenantCd);
    };

    refreshPublishedCohorts();

    return () => {
      cancelled = true;
      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [open, returnFromCohortBuilder, cohortWasPublished, effectiveTenantValue?.code, dispatch, reduxStore]);

  const handleCohortSelectionChange = React.useCallback(async (selectedLuid) => {
    shouldRefreshCohortPersonIdsRef.current = false;
    setPopulationMode(POPULATION_MODE.COHORT);
    setCohortValue(selectedLuid || '');

    if (!selectedLuid) {
      latestCohortRequestIdRef.current += 1;
      pendingCohortLuidRef.current = null;
      setIsCohortPersonIdsLoading(false);
      setPersonIdValue('');
      clearPersonIdValidationState();
      setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      return;
    }

    if (pendingCohortLuidRef.current === selectedLuid) {
      return;
    }

    if (!effectiveTenantValue?.code) {
      pendingCohortLuidRef.current = null;
      setPersonIdValue('');
      clearPersonIdValidationState();
      setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      return;
    }

    const requestId = latestCohortRequestIdRef.current + 1;
    latestCohortRequestIdRef.current = requestId;
    pendingCohortLuidRef.current = selectedLuid;
    setPersonIdValue('');
    clearPersonIdValidationState();
    setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
    setIsCohortPersonIdsLoading(true);

    try {
      const res = await axios.post(
        AppConfig.FO_INITIAL_URL + '/cohorts/person-ids',
        { tenantCd: effectiveTenantValue.code, luid: selectedLuid },
        { hideSpinner: true }
      );

      if (latestCohortRequestIdRef.current !== requestId) {
        return;
      }

      const fetchedPersonIds = Array.isArray(res?.data?.personIds) ? res.data.personIds : [];
      const fetchedPersonIdValue = fetchedPersonIds.join(',');
      setPersonIdValue(fetchedPersonIdValue);
      await validatePersonIdsAgainstContext({ personIdInput: fetchedPersonIdValue });
    } catch (error) {
      if (latestCohortRequestIdRef.current !== requestId) {
        return;
      }
      setPersonIdValue('');
      clearPersonIdValidationState();
      setFieldErrors((currentValue) => ({ ...currentValue, personId: COHORT_PERSON_ID_FETCH_ERROR }));
    } finally {
      if (latestCohortRequestIdRef.current === requestId) {
        pendingCohortLuidRef.current = null;
        setIsCohortPersonIdsLoading(false);
      }
    }
  }, [effectiveTenantValue?.code, clearPersonIdValidationState, validatePersonIdsAgainstContext]);

  React.useEffect(() => {
    if (!focusCohortSelectionRequestKey) {
      return;
    }

    // Treat focusCohortSelectionRequestKey as a one-shot event token:
    // - A return flow sets a non-null key so PopulationFilters can restore focus.
    // - We clear it on the next macrotask (delay 0) so consumers get one turn to
    //   observe the key, then the request expires quickly.
    // - Clearing synchronously can race with consumers and cause missed focus.
    // - Keeping it longer can leak stale focus requests into unrelated updates
    //   (e.g., tenant changes where Population appears later).
    const timeoutId = window.setTimeout(() => {
      setFocusCohortSelectionRequestKey(null);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [focusCohortSelectionRequestKey]);

  React.useEffect(() => {
    const isCohortOptionsForEffectiveTenant = Boolean(
      effectiveTenantValue?.code && cohortOptionsTenantCd === effectiveTenantValue.code
    );
    const hasCompletedPublishedRefresh = publishedCohortRefreshTenantCd === effectiveTenantValue?.code;

    if (!open || !returnFromCohortBuilder || !cohortWasPublished || !cohortOptions || !hasCompletedPublishedRefresh || !isCohortOptionsForEffectiveTenant) {
      return;
    }

    // Compare the newest item from each category using the raw data (which
    // retains updateTime and is already sorted desc by updateTime).
    const newestPersonal = Array.isArray(cohortPersonalRaw) ? cohortPersonalRaw[0] : null;
    const newestPublic = Array.isArray(cohortPublicRaw) ? cohortPublicRaw[0] : null;

    if (!newestPersonal && !newestPublic) {
      publishedCohortBaselineRef.current = null;
      requestCohortBuilderReturnFocus();
      if (onCohortBuilderReturnConsumed) {
        onCohortBuilderReturnConsumed();
      }
      return;
    }

    const personalEpoch = newestPersonal ? toCohortUpdateEpoch(newestPersonal.updateTime) : 0;
    const publicEpoch = newestPublic ? toCohortUpdateEpoch(newestPublic.updateTime) : 0;

    const newestCohortLuid = personalEpoch >= publicEpoch
      ? newestPersonal?.luid
      : newestPublic?.luid;

    // Prefer the cohort the pre-publish baseline identifies as new or updated;
    // the newest cohort overall is only a fallback for flows without a baseline.
    const publishedCohortLuid = findPublishedCohortLuid(
      cohortPersonalRaw,
      cohortPublicRaw,
      publishedCohortBaselineRef.current
    );
    const targetCohortLuid = publishedCohortLuid || newestCohortLuid;

    if (targetCohortLuid) {
      handleCohortSelectionChange(targetCohortLuid);
      requestCohortBuilderReturnFocus();
    }

    publishedCohortBaselineRef.current = null;
    if (onCohortBuilderReturnConsumed) {
      onCohortBuilderReturnConsumed();
    }
  }, [open, returnFromCohortBuilder, cohortWasPublished, cohortOptions, cohortPersonalRaw, cohortPublicRaw, cohortOptionsTenantCd, publishedCohortRefreshTenantCd, effectiveTenantValue?.code, handleCohortSelectionChange, onCohortBuilderReturnConsumed, requestCohortBuilderReturnFocus]);

  // Previews the tenant's saved global filter (if any, and not expired) into the
  // drawer's local state when a different tenant is chosen. This is a draft only:
  // nothing is committed to the store or persisted until the user clicks Apply.
  // Date basis/time window and the population selection are restored; the date
  // range is restored verbatim only for CUSTOM windows (named windows are
  // recomputed from the tenant's latest data by the date effects). Division/plan
  // options reload fresh for the new tenant.
  const previewSavedFilter = React.useCallback(async (tenantCd) => {
    const requestId = latestPreviewRequestIdRef.current + 1;
    latestPreviewRequestIdRef.current = requestId;
    setIsSavedFilterPreviewLoading(true);

    const savedFilter = await fetchSavedFilter(tenantCd);

    // Ignore a stale response if the tenant changed again while loading.
    if (latestPreviewRequestIdRef.current !== requestId) {
      return;
    }

    if (!savedFilter) {
      setEnablePriorComparison(defaultAppliedFilterValues.enablePriorComparison);
      setIsSavedFilterPreviewLoading(false);
      return;
    }

    // Restore the saved division/plan selections so option hydration checks only
    // the previously selected labels (instead of defaulting all to checked).
    setPreviewedSelection({
      divisionLabels: Array.isArray(savedFilter.divisionLabels) ? savedFilter.divisionLabels : [],
      planLabels: Array.isArray(savedFilter.planLabels) ? savedFilter.planLabels : [],
    });

    setDateBasisValue(savedFilter.dateBasis || defaultAppliedFilterValues.dateBasis);
    setEnablePriorComparison(savedFilter.enablePriorComparison !== false);

    const savedTimeWindow = savedFilter.timeWindow || defaultAppliedFilterValues.timeWindow;
    setTimeWindowValue(savedTimeWindow);
    if (savedTimeWindow === 'CUSTOM') {
      setCurrentPeriodFromValue(savedFilter.currentPeriodFrom || defaultAppliedFilterValues.currentPeriodFrom);
      setCurrentPeriodToValue(savedFilter.currentPeriodTo || defaultAppliedFilterValues.currentPeriodTo);
      setPriorPeriodFromValue(savedFilter.priorPeriodFrom || defaultAppliedFilterValues.priorPeriodFrom);
      setPriorPeriodToValue(savedFilter.priorPeriodTo || defaultAppliedFilterValues.priorPeriodTo);
    }
    setIsSavedFilterPreviewLoading(false);

    if (savedFilter.populationMode === POPULATION_MODE.COHORT && savedFilter.cohortLuid) {
      // Restore the cohort selection and re-derive its current person ids.
      setPopulationMode(POPULATION_MODE.COHORT);
      setCohortValue(savedFilter.cohortLuid);

      // Detect a deleted cohort by reading the tenant-tagged cohort options from
      // the store. PopulationFilters already fetches these on tenant change, so we
      // only fetch here when the store does not yet hold this tenant's options.
      // fetchCohortOptions coalesces concurrent calls, so at most one network
      // request happens per tenant change. The tenant tag also guards against
      // acting on a failed refresh. If the saved cohort is no longer present, clear
      // the selection and skip the person-id fetch instead of restoring a cohort
      // that no longer exists.
      if (reduxStore.getState()?.fo?.cohortOptionsTenantCd !== tenantCd) {
        await dispatch(fetchCohortOptions(tenantCd));
        if (latestPreviewRequestIdRef.current !== requestId) {
          return;
        }
      }
      const foState = reduxStore.getState()?.fo || {};
      const areOptionsForTenant = foState.cohortOptionsTenantCd === tenantCd;
      const isCohortStillAvailable = Object.values(foState.cohortOptions || {})
        .flat()
        .some((option) => option?.value === String(savedFilter.cohortLuid));
      if (areOptionsForTenant && !isCohortStillAvailable) {
        clearCohortSelection();
        return;
      }

      const cohortRequestId = latestCohortRequestIdRef.current + 1;
      latestCohortRequestIdRef.current = cohortRequestId;
      pendingCohortLuidRef.current = savedFilter.cohortLuid;
      setPersonIdValue('');
      setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      setIsCohortPersonIdsLoading(true);

      try {
        const res = await axios.post(
          AppConfig.FO_INITIAL_URL + '/cohorts/person-ids',
          { tenantCd, luid: savedFilter.cohortLuid },
          { hideSpinner: true }
        );

        if (latestCohortRequestIdRef.current !== cohortRequestId) {
          return;
        }

        const fetchedPersonIds = Array.isArray(res?.data?.personIds) ? res.data.personIds : [];
        setPersonIdValue(fetchedPersonIds.join(','));
        clearPersonIdValidationState();
        setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      } catch (error) {
        if (latestCohortRequestIdRef.current !== cohortRequestId) {
          return;
        }
        // Saved cohort can no longer be resolved: fall back to no population silently.
        setPopulationMode(null);
        setCohortValue('');
        setPersonIdValue('');
        clearPersonIdValidationState();
        setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
      } finally {
        if (latestCohortRequestIdRef.current === cohortRequestId) {
          pendingCohortLuidRef.current = null;
          setIsCohortPersonIdsLoading(false);
        }
      }
    } else if (savedFilter.personIdValue) {
      setPopulationMode(POPULATION_MODE.PERSON_ID);
      setCohortValue('');
      clearPersonIdValidationState();
      setPersonIdValue(savedFilter.personIdValue);
      setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
    }
  }, [clearPersonIdValidationState, clearCohortSelection, dispatch, reduxStore]);

  // Detect a deleted cohort on both the restore-on-open and tenant-switch paths.
  // Once the tenant's cohort options have been freshly fetched (tagged with the
  // tenant they belong to), clear the cohort selection if the previously selected
  // cohort is no longer present so its stale person ids are never applied.
  React.useEffect(() => {
    if (populationMode !== POPULATION_MODE.COHORT || !cohortValue) {
      return;
    }

    const tenantCd = effectiveTenantValue?.code;
    if (!tenantCd || cohortOptionsTenantCd !== tenantCd) {
      return;
    }

    const isCohortStillAvailable = Object.values(cohortOptions || {})
      .flat()
      .some((option) => option?.value === String(cohortValue));

    if (!isCohortStillAvailable) {
      shouldRefreshCohortPersonIdsRef.current = false;
      clearCohortSelection();
      return;
    }

    // Cohort is valid: re-derive its person ids from the cohort's current members
    // when it was just loaded from a saved/applied filter (restore-on-open).
    if (shouldRefreshCohortPersonIdsRef.current) {
      shouldRefreshCohortPersonIdsRef.current = false;
      handleCohortSelectionChange(cohortValue);
    }
  }, [open, populationMode, cohortValue, effectiveTenantValue?.code, cohortOptions, cohortOptionsTenantCd, clearCohortSelection, handleCohortSelectionChange]);

  React.useEffect(() => {
    if (!open || !effectiveTenantValue?.code || !areTenantFiltersEnabled || isDateRangeEditing) {
      return undefined;
    }

    if (!(cascadeMode || optionAvailabilityModeRef.current)) {
      return undefined;
    }

    const effectiveDateRangeValues = getEffectiveDateRangeValues();

    const payload = buildOptionsBasePayload({
      tenantCd: effectiveTenantValue.code,
      dateBasisValue,
      ...effectiveDateRangeValues,
    });
    const requestKey = JSON.stringify(payload);

    if (
      optionsBaselineCache[requestKey] ||
      requestKey === pendingUnfilteredOptionsRequestKeyRef.current
    ) {
      return undefined;
    }
    pendingUnfilteredOptionsRequestKeyRef.current = requestKey;

    Promise.resolve()
      .then(() => axios.post(AppConfig.FO_INITIAL_URL + '/global-filter/options', payload, { hideSpinner: true }))
      .then((res) => {
        if (pendingUnfilteredOptionsRequestKeyRef.current !== requestKey) {
          return;
        }

        if (!res?.data?.filterOptions) {
          return;
        }

        cacheBaselineOptions(requestKey, normalizeGlobalFilterOptions(res.data.filterOptions));
      })
      .catch((err) => {
        if (pendingUnfilteredOptionsRequestKeyRef.current !== requestKey) {
          return;
        }

        /* A tenant switch cancellation must stay retryable, not be cached as if
           this tenant/app combination has no available options. Piggyback on the
           same canceled-retry mechanism as fetchGlobalFilterOptions so the retry
           effect below bumps optionsRetryTick (a dependency of this effect) once
           the switch settles, even when the tenant itself doesn't change. */
        if (axios.isCancel(err)) {
          pendingCanceledRetryRef.current = requestKey;
          return;
        }

        cacheBaselineOptions(requestKey, emptyGlobalFilterOptions);
      })
      .finally(() => {
        if (pendingUnfilteredOptionsRequestKeyRef.current === requestKey) {
          pendingUnfilteredOptionsRequestKeyRef.current = null;
        }
      });

    return undefined;
  }, [
    open,
    effectiveTenantValue?.code,
    areTenantFiltersEnabled,
    isDateRangeEditing,
    dateBasisValue,
    currentPeriodFromValue,
    currentPeriodToValue,
    priorPeriodFromValue,
    priorPeriodToValue,
    getEffectiveDateRangeValues,
    cascadeMode,
    optionsBaselineCache,
    cacheBaselineOptions,
    optionsRetryTick,
  ]);

  React.useEffect(() => {
    if (!open || !effectiveTenantValue?.code || !areTenantFiltersEnabled || isDateRangeEditing) {
      return undefined;
    }

    // Avoid overlapping /options requests; we'll re-run when loading clears.
    if (isGlobalFilterOptionsLoading && globalFilterOptionsRequestKey) {
      return undefined;
    }

    if (suppressNextOptionsFetchRef.current) {
      suppressNextOptionsFetchRef.current = false;
      return undefined;
    }

    const datePayload = buildOptionsDatePayload(getEffectiveDateRangeValues());

    const selectedDivisionLabels = divisionValue
      .slice(1)
      .filter((option) => option.checked)
      .map((option) => option.label);
    const selectedPlanLabels = planValue
      .slice(1)
      .filter((option) => option.checked)
      .map((option) => option.label);

    const hasDivisionSelection = selectedDivisionLabels.length > 0;
    const hasPlanSelection = selectedPlanLabels.length > 0;

    const payload = {
      tenantCd: effectiveTenantValue.code,
      dateBasis: dateBasisValue,
      ...datePayload,
      ...(cascadeMode === 'division' && hasDivisionSelection ? { division: selectedDivisionLabels } : {}),
      ...(cascadeMode === 'plan' && hasPlanSelection ? { plan: selectedPlanLabels } : {})
    };

    const requestKey = JSON.stringify(payload);
    const isSkipped = requestKey === lastOptionsRequestKeyRef.current || requestKey === globalFilterOptionsRequestKey || requestKey === globalFilterOptionsLastSuccessKey;
    if (isSkipped) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (requestKey !== lastOptionsRequestKeyRef.current) {
        lastOptionsRequestKeyRef.current = requestKey;
        dispatch(fetchGlobalFilterOptions({ requestKey, payload }));
      }
    }, 300);
    optionsFetchTimeoutRef.current = timeoutId;

    return () => {
      window.clearTimeout(timeoutId);
      if (optionsFetchTimeoutRef.current === timeoutId) {
        optionsFetchTimeoutRef.current = null;
      }
    };
  }, [
    open,
    effectiveTenantValue?.code,
    dateBasisValue,
    currentPeriodFromValue,
    currentPeriodToValue,
    priorPeriodFromValue,
    priorPeriodToValue,
    getEffectiveDateRangeValues,
    divisionValue,
    planValue,
    isDivisionTouched,
    isPlanTouched,
    cascadeMode,
    areTenantFiltersEnabled,
    isDateRangeEditing,
    isGlobalFilterOptionsLoading,
    globalFilterOptionsRequestKey,
    globalFilterOptionsLastSuccessKey,
    optionsRetryTick,
    dispatch
  ]);

  React.useEffect(() => {
    if (!open) {
      lastHandledOptionsSuccessKeyRef.current = null;
      return;
    }

    if (!globalFilterOptionsLastSuccessKey || globalFilterOptionsLastSuccessKey === lastHandledOptionsSuccessKeyRef.current) {
      return;
    }

    lastHandledOptionsSuccessKeyRef.current = globalFilterOptionsLastSuccessKey;

    // Only reset when the success corresponds to this drawer's most recent options request.
    if (globalFilterOptionsLastSuccessKey !== lastOptionsRequestKeyRef.current) {
      return;
    }

    // After /options is fetched, clear cascadeMode so future date edits don't accidentally
    // keep filtering options using the last programmatic cascade.
    if (cascadeMode !== null) {
      suppressNextOptionsFetchRef.current = true;
      setCascadeMode(null);
      // Also clear the ref to ensure stale availability mode doesn't persist
      optionAvailabilityModeRef.current = null;
      // Clear hydration keys to force re-hydration with fresh state after cascade is cleared
      lastHydratedOptionsKeyRef.current = null;
      lastHydratedBaselineKeyRef.current = null;
    }
  }, [open, globalFilterOptionsLastSuccessKey, cascadeMode]);

  /*
   * A tenant switch cancels in-flight /options requests. Remember that it
   * happened, then re-request once the switch has settled (retrying while it is
   * still in flight would just be cancelled again).
   */
  React.useEffect(() => {
    if (!globalFilterOptionsCanceledKey) {
      return;
    }
    pendingCanceledRetryRef.current = globalFilterOptionsCanceledKey;
  }, [globalFilterOptionsCanceledKey]);

  React.useEffect(() => {
    if (!pendingCanceledRetryRef.current) {
      return;
    }

    if (lastOptionsRequestKeyRef.current === pendingCanceledRetryRef.current) {
      lastOptionsRequestKeyRef.current = '';
    }
    pendingUnfilteredOptionsRequestKeyRef.current = null;
    pendingCanceledRetryRef.current = null;
    setOptionsRetryTick((tick) => tick + 1);
  }, [currentAppCode, currentTenantCd]);

  const validateDateRange = (dateValueOverrides = {}) => {
    const effectiveDateRangeValues = getEffectiveDateRangeValues(dateValueOverrides);
    return getDateRangeValidationResult(
      isPriorComparisonActive
        ? effectiveDateRangeValues
        : {
          ...effectiveDateRangeValues,
          priorPeriodFromValue: '',
          priorPeriodToValue: '',
        },
      { allowEmptyPriorPeriod }
    );
  };

  const validateDateRangeOnBlur = () => {
    const currentDateOptionError = dateOptionErrorRef.current;
    // Use raw state values (not normalized) for the option-error value check and for
    // R12 format validation. For R12, getEffectiveDateRangeValues() normalizes the
    // Current Period To away (recalculates from From), so checking against it would
    // always miss an invalid typed value.
    const rawDateValues = {
      currentPeriodFromValue,
      currentPeriodToValue,
      priorPeriodFromValue,
      priorPeriodToValue,
    };
    const hasCurrentDateOptionError = Boolean(
      currentDateOptionError
      && rawDateValues[currentDateOptionError.fieldName] === currentDateOptionError.value
    );
    // For R12, explicitly detect an invalid or empty Current Period To before calling
    // validateDateRange(), which would normalize it away and report no error.
    // Note: isValidMonthValue('') returns false, so this also catches cleared/empty values.
    const trimmedCurrentPeriodToValue = String(currentPeriodToValue || '').trim();
    const hasInvalidRawR12To = timeWindowValue === 'R12' && !isValidMonthValue(trimmedCurrentPeriodToValue);
    const { message: dateRangeError, invalidFields: invalidDateRangeFields } = hasInvalidRawR12To
      ? { message: FORMAT_ERROR, invalidFields: { currentPeriodTo: true } }
      : validateDateRange();

    setFieldErrors((currentValue) => ({
      ...currentValue,
      dateRange: dateRangeError || (hasCurrentDateOptionError ? DATE_OPTION_ERROR : null),
      dateRangeFields: dateRangeError
        ? invalidDateRangeFields
        : hasCurrentDateOptionError
          ? { [currentDateOptionError.errorKey]: true }
          : {},
    }));
  };

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const nextDivisionLabels = globalFilterOptions?.division;
    const nextPlanLabels = globalFilterOptions?.plan;

    const isClearAllSuccess = Boolean(didClearAll) && Boolean(clearAllRequestKeyRef.current) && clearAllRequestKeyRef.current === globalFilterOptionsLastSuccessKey;
    const isDivisionPlanResetSuccess = Boolean(resetDivisionPlanRequestKeyRef.current) && resetDivisionPlanRequestKeyRef.current === globalFilterOptionsLastSuccessKey;

    // Clear All flow: wait for the refreshed options response before hydrating.
    if (didClearAll && !isClearAllSuccess) {
      // Detect options-request failure: loading finished but the success key
      // never matched our Clear All key.  Abandon the gate so division/plan
      // are not permanently empty.
      if (
        clearAllRequestKeyRef.current &&
        !isGlobalFilterOptionsLoading &&
        globalFilterOptionsRequestKey !== clearAllRequestKeyRef.current
      ) {
        clearAllRequestKeyRef.current = null;
        lastHydratedOptionsKeyRef.current = null;
        lastHydratedBaselineKeyRef.current = null;
        setDidClearAll(false);
      }
      return;
    }

    const optionsHydrationKey = JSON.stringify({
      division: Array.isArray(nextDivisionLabels) ? nextDivisionLabels : null,
      plan: Array.isArray(nextPlanLabels) ? nextPlanLabels : null,
      successKey: globalFilterOptionsLastSuccessKey || null,
      previewedSelection: previewedSelection || null,
    });
    const optionsBasePayload = buildOptionsBasePayload({
      tenantCd: effectiveTenantValue?.code,
      dateBasisValue,
      ...getEffectiveDateRangeValues(),
    });
    const optionsBaseRequestKey = JSON.stringify(optionsBasePayload);

    const baselineHydrationKey = optionsBaselineCache[optionsBaseRequestKey] ? optionsBaseRequestKey : null;
    const didOptionsChange = optionsHydrationKey !== lastHydratedOptionsKeyRef.current;
    const didBaselineChange = baselineHydrationKey !== lastHydratedBaselineKeyRef.current;

    if (!didOptionsChange && !didBaselineChange) {
      return;
    }

    lastHydratedOptionsKeyRef.current = optionsHydrationKey;
    lastHydratedBaselineKeyRef.current = baselineHydrationKey;

    // Cache baseline options first (before checking stale hydration).
    // This ensures baseline options are always cached, even if we skip hydration.
    const shouldReuseOptionsAsBaseline = didOptionsChange && globalFilterOptionsLastSuccessKey === optionsBaseRequestKey;
    if (shouldReuseOptionsAsBaseline && !optionsBaselineCache[optionsBaseRequestKey]) {
      const baselineOpts = normalizeGlobalFilterOptions(globalFilterOptions || {});
      cacheBaselineOptions(optionsBaseRequestKey, baselineOpts);
    }

    // The narrowed Redux options (globalFilterOptions) are a single "latest response"
    // slot that is NOT tenant-keyed. After a tenant switch the store still holds the
    // previous tenant's labels until the new tenant's /options fetch resolves.
    const successKeyTenantCd = (() => {
      if (!globalFilterOptionsLastSuccessKey) {
        return null;
      }
      try {
        return JSON.parse(globalFilterOptionsLastSuccessKey)?.tenantCd ?? null;
      } catch {
        return null;
      }
    })();
    const isSuccessKeyForCurrentTenant = !globalFilterOptionsLastSuccessKey || successKeyTenantCd === effectiveTenantValue?.code;
    if (!isSuccessKeyForCurrentTenant) {
      return;
    }

    // After returning from Cohort Builder, keep the restored Division/Plan draft
    // intact until the user explicitly changes those fields (or clears/resets).
    if (freezeDivisionPlanHydrationRef.current) {
      return;
    }

    // On reopen, ignore stale narrowed /options responses (from the previous open cycle)
    // until the baseline response for the current base payload arrives.
    const isUntouchedOnOpenCycle = !didClearAll && !isDivisionTouched && !isPlanTouched && !previewedSelection;
    const hasStaleOptionsSuccessKey = Boolean(globalFilterOptionsLastSuccessKey) && globalFilterOptionsLastSuccessKey !== optionsBaseRequestKey;
    if (isUntouchedOnOpenCycle && hasStaleOptionsSuccessKey) {
      return;
    }
    // Case 1: Tenant changed → default all options to checked, unless a saved
    //         filter was previewed for the new tenant, in which case restore its
    //         division/plan selections.
    // Case 2: Date range change → preserve previous checkbox state.
    // Case 3: Plan toggle → preserve plan toggle + division state.
    // Case 4: Division toggle → preserve division toggle + plan state.
    const hasTenantChanged = effectiveTenantValue?.code !== currentTenantCd;
    const restoreBaseFilters = restoreBaseFiltersRef.current || appliedFilters;
    const hasPreviewedSelection = previewedSelection != null;
    const previewedDivisionLabels = hasPreviewedSelection ? previewedSelection.divisionLabels : undefined;
    const previewedPlanLabels = hasPreviewedSelection ? previewedSelection.planLabels : undefined;
    const cohortDraftSelectionOverride = cohortDraftSelectionOverrideRef.current;
    const hasCohortDraftSelectionOverride = Boolean(cohortDraftSelectionOverride);
    const shouldPreferCohortDraftSelection = Boolean(
      preferCohortDraftSelectionRef.current && hasCohortDraftSelectionOverride
    );
    const shouldForceCohortDraftSelection = Boolean(
      cohortDraftForceSelectionRef.current && hasCohortDraftSelectionOverride
    );
    const cohortDraftDivisionLabels = hasCohortDraftSelectionOverride
      ? (Array.isArray(cohortDraftSelectionOverride.divisionLabels) ? cohortDraftSelectionOverride.divisionLabels : [])
      : undefined;
    const cohortDraftPlanLabels = hasCohortDraftSelectionOverride
      ? (Array.isArray(cohortDraftSelectionOverride.planLabels) ? cohortDraftSelectionOverride.planLabels : [])
      : undefined;

    let initialDivisionSelection;
    let initialPlanSelection;
    if (didClearAll) {
      initialDivisionSelection = undefined;
      initialPlanSelection = undefined;
    } else if (hasCohortDraftSelectionOverride) {
      initialDivisionSelection = (shouldForceCohortDraftSelection || shouldPreferCohortDraftSelection || !isDivisionTouched)
        ? cohortDraftDivisionLabels
        : undefined;
      initialPlanSelection = (shouldForceCohortDraftSelection || shouldPreferCohortDraftSelection || !isPlanTouched)
        ? cohortDraftPlanLabels
        : undefined;
    } else if (hasTenantChanged) {
      initialDivisionSelection = hasPreviewedSelection && !isDivisionTouched ? previewedDivisionLabels : undefined;
      initialPlanSelection = hasPreviewedSelection && !isPlanTouched ? previewedPlanLabels : undefined;
    } else {
      initialDivisionSelection = !isDivisionTouched ? restoreBaseFilters?.divisionLabels : undefined;
      initialPlanSelection = !isPlanTouched ? restoreBaseFilters?.planLabels : undefined;
    }

    // On a fresh baseline options response, we may force all checked only when the
    // applied state does not carry any explicit partial Division/Plan selection.
    // This prevents overriding the user's applied subset after returning from
    // Cohort Builder or reopening the drawer.
    const isOptionsChangeFromBaseline = didOptionsChange && (globalFilterOptionsLastSuccessKey === optionsBaseRequestKey);
    const hasAppliedPartialDivisionSelection =
      Array.isArray(restoreBaseFilters?.divisionLabels)
      && restoreBaseFilters.divisionLabels.length > 0
      && restoreBaseFilters?.isAllDivisionsSelected === false;
    const hasAppliedPartialPlanSelection =
      Array.isArray(restoreBaseFilters?.planLabels)
      && restoreBaseFilters.planLabels.length > 0
      && restoreBaseFilters?.isAllPlansSelected === false;
    // AGENT-CONTEXT: A fresh baseline /options response must NEVER override a
    // saved filter previewed for a newly chosen tenant. Without the
    // !hasPreviewedSelection guard, switching from a tenant whose applied filters
    // were fully selected (isAllDivisions/PlansSelected === true, so the
    // hasAppliedPartial* guards are false) would force-all-checked and wipe the
    // restored preview a moment after it loads. Override only if the force-all
    // path is intentionally decoupled from tenant-switch preview restoration.
    const shouldForceAllCheckedOnFreshOptions =
      isOptionsChangeFromBaseline
      && !cascadeMode
      && !isDivisionTouched
      && !isPlanTouched
      && !shouldPreferCohortDraftSelection
      && !shouldForceCohortDraftSelection
      && !hasCohortDraftSelectionOverride
      && !hasPreviewedSelection
      && !hasAppliedPartialDivisionSelection
      && !hasAppliedPartialPlanSelection;

    const shouldForceAllChecked =
      isClearAllSuccess ||
      isDivisionPlanResetSuccess ||
      (hasTenantChanged
        && !isDivisionTouched
        && !isPlanTouched
        && !hasPreviewedSelection
        && !hasCohortDraftSelectionOverride
        && !shouldForceCohortDraftSelection
        && !shouldPreferCohortDraftSelection) ||
      shouldForceAllCheckedOnFreshOptions;
    // When a saved filter was previewed for the newly chosen tenant, restore its
    // selections verbatim even if a prior pass defaulted everything to checked.
    const shouldRestoreDivisionPreview =
      shouldForceCohortDraftSelection
      || (!isDivisionTouched && ((hasTenantChanged && hasPreviewedSelection) || hasCohortDraftSelectionOverride));
    const shouldRestorePlanPreview =
      shouldForceCohortDraftSelection
      || (!isPlanTouched && ((hasTenantChanged && hasPreviewedSelection) || hasCohortDraftSelectionOverride));
    
    // Only use cascadeMode if it's set (meaning there's an explicit cascade filter).
    // Don't use optionAvailabilityModeRef.current as fallback, as it may be stale from previous interactions.
    const availabilityMode = cascadeMode;
    // Only preserve unavailable options when we're actively in a cascade mode (user is filtering).
    // Don't preserve if this is a fresh baseline fetch or a reopened drawer with no active cascade.
    const shouldPreserveUnavailableDivisions = !shouldForceAllChecked && availabilityMode === 'plan' && !isOptionsChangeFromBaseline;
    const shouldPreserveUnavailablePlans = !shouldForceAllChecked && availabilityMode === 'division' && !isOptionsChangeFromBaseline;
    const cachedBaselineOptions = optionsBaselineCache[optionsBaseRequestKey] ?? emptyGlobalFilterOptions;
    const baselineOptions = shouldReuseOptionsAsBaseline
      ? normalizeGlobalFilterOptions(globalFilterOptions || {})
      : cachedBaselineOptions;

    const shouldHydrateDivisions = didOptionsChange || (didBaselineChange && shouldPreserveUnavailableDivisions && hasOptionLabels(baselineOptions.division));
    const shouldHydratePlans = didOptionsChange || (didBaselineChange && shouldPreserveUnavailablePlans && hasOptionLabels(baselineOptions.plan));

    if (shouldHydrateDivisions && Array.isArray(nextDivisionLabels)) {
      if (nextDivisionLabels.length > 0) {
        if (shouldForceAllChecked) {
          // Case 1 / Clear All: check all division options.
          setDivisionValue(buildAllCheckedChecklistOptions('All Divisions', nextDivisionLabels));
        } else {
          // Cases 2, 3, 4: preserve previous checkbox state via currentValue.
          setDivisionValue((currentValue) => {
            const result = buildChecklistOptions({
              allLabel: 'All Divisions',
              optionLabels: nextDivisionLabels,
              currentValue,
              isTouched: isDivisionTouched,
              selectedLabelsOverride: initialDivisionSelection,
              preserveUnavailable: shouldPreserveUnavailableDivisions,
              baselineOptionLabels: baselineOptions.division,
              forceSelectionOverride: shouldRestoreDivisionPreview,
            });
            return result;
          });
        }
      } else {
        // Backend returned no divisions for the current tenant/date; clear any stale selections.
        setDivisionValue(divisionOptions.map((option) => ({ ...option })));
      }
    }

    if (shouldHydratePlans && Array.isArray(nextPlanLabels)) {
      if (nextPlanLabels.length > 0) {
        if (shouldForceAllChecked) {
          // Case 1 / Clear All: check all plan options.
          setPlanValue(buildAllCheckedChecklistOptions('All Plans', nextPlanLabels));
        } else {
          // Cases 2, 3, 4: preserve previous checkbox state via currentValue.
          setPlanValue((currentValue) => {
            const result = buildChecklistOptions({
              allLabel: 'All Plans',
              optionLabels: nextPlanLabels,
              currentValue,
              isTouched: isPlanTouched,
              selectedLabelsOverride: initialPlanSelection,
              preserveUnavailable: shouldPreserveUnavailablePlans,
              baselineOptionLabels: baselineOptions.plan,
              forceSelectionOverride: shouldRestorePlanPreview,
            });
            return result;
          });
        }
      } else {
        // Backend returned no plans for the current tenant/date; clear any stale selections.
        setPlanValue(planOptions.map((option) => ({ ...option })));
      }
    }

    if (isClearAllSuccess) {
      clearAllRequestKeyRef.current = null;
      setDidClearAll(false);
    }

    if (isDivisionPlanResetSuccess) {
      resetDivisionPlanRequestKeyRef.current = null;
    }

    if (shouldForceCohortDraftSelection && (shouldHydrateDivisions || shouldHydratePlans)) {
      cohortDraftForceSelectionRef.current = false;
    }
  }, [
    open,
    appliedFilters,
    globalFilterOptions,
    isDivisionTouched,
    isPlanTouched,
    cascadeMode,
    didClearAll,
    globalFilterOptionsLastSuccessKey,
    currentTenantCd,
    effectiveTenantValue?.code,
    dateBasisValue,
    currentPeriodFromValue,
    currentPeriodToValue,
    priorPeriodFromValue,
    priorPeriodToValue,
    getEffectiveDateRangeValues,
    optionsBaselineCache,
    previewedSelection,
    isGlobalFilterOptionsLoading,
    globalFilterOptionsRequestKey,
    cacheBaselineOptions,
    optionsRetryTick,
  ]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const shouldValidateDivision = isDivisionTouched || Boolean(fieldErrors.division);
    if (!shouldValidateDivision) {
      return;
    }

    if (divisionValue.length <= 1) {
      return;
    }

    const nextDivisionError = hasAnyNonAllOptionChecked(divisionValue) ? null : 'Select at least one Division.';
    setFieldErrors((currentValue) => (currentValue.division === nextDivisionError ? currentValue : { ...currentValue, division: nextDivisionError }));
  }, [divisionValue, isDivisionTouched, fieldErrors.division, open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const shouldValidatePlan = isPlanTouched || Boolean(fieldErrors.plan);
    if (!shouldValidatePlan) {
      return;
    }

    if (planValue.length <= 1) {
      return;
    }

    const nextPlanError = hasAnyNonAllOptionChecked(planValue) ? null : 'Select at least one Plan.';
    setFieldErrors((currentValue) => (currentValue.plan === nextPlanError ? currentValue : { ...currentValue, plan: nextPlanError }));
  }, [planValue, isPlanTouched, fieldErrors.plan, open]);

  const handlePersonIdChange = (event) => {
    setPersonIdValue(event.target.value);
    clearPersonIdValidationState();
    setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
  };

  const handlePersonIdBlur = async () => {
    const validationResult = await validatePersonIdsAgainstContext();
    if (!validationResult.ok) {
      return;
    }
  };

  const handlePersonIdReset = () => {
    setPersonIdValue('');
    clearPersonIdValidationState();
    setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
  };

  const handlePopulationReset = () => {
    latestCohortRequestIdRef.current += 1;
    pendingCohortLuidRef.current = null;
    shouldRefreshCohortPersonIdsRef.current = false;
    setIsCohortPersonIdsLoading(false);
    setPopulationMode(null);
    setCohortValue('');
    setPersonIdValue('');
    clearPersonIdValidationState();
    setFieldErrors((currentValue) => ({ ...currentValue, personId: null }));
  };

  const handlePopulationModeChange = (mode) => {
    if (mode === POPULATION_MODE.COHORT) {
      setPersonIdValue('');
      clearPersonIdValidationState();
      setFieldErrors((current) => ({ ...current, personId: null }));
    } else if (mode === POPULATION_MODE.PERSON_ID) {
      latestCohortRequestIdRef.current += 1;
      pendingCohortLuidRef.current = null;
      setIsCohortPersonIdsLoading(false);
      setCohortValue('');
      clearPersonIdValidationState();
    }
    setPopulationMode(mode);
  };

  const handleDivisionToggle = (index) => {
    if (!open || !effectiveTenantValue?.code || !areTenantFiltersEnabled) {
      return;
    }

    setDidClearAll(false);
    preferCohortDraftSelectionRef.current = false;
    cohortDraftSelectionOverrideRef.current = null;
    cohortDraftForceSelectionRef.current = false;
    restoreBaseFiltersRef.current = null;
    freezeDivisionPlanHydrationRef.current = false;
    clearPersonIdValidationState({ preserveMessage: true });
    setIsDivisionTouched(true);
    setCascadeMode('division');
    optionAvailabilityModeRef.current = 'division';

    const nextDivisionValue = toggleSelectionOptions(divisionValue, index);
    setDivisionValue(nextDivisionValue);

    const selectedDivisionLabels = nextDivisionValue
      .slice(1)
      .filter((option) => option.checked)
      .map((option) => option.label);

    const hasDivisionSelection = selectedDivisionLabels.length > 0;

    const payload = {
      tenantCd: effectiveTenantValue.code,
      dateBasis: dateBasisValue,
      ...buildOptionsDatePayload(getEffectiveDateRangeValues()),
      ...(hasDivisionSelection ? { division: selectedDivisionLabels } : {})
    };

    const requestKey = JSON.stringify(payload);
    const hasNewerRequestInFlight = Boolean(globalFilterOptionsRequestKey) && globalFilterOptionsRequestKey !== requestKey;
    const matchesSettledResult = requestKey === globalFilterOptionsLastSuccessKey && !hasNewerRequestInFlight;
    if (requestKey !== lastOptionsRequestKeyRef.current && requestKey !== globalFilterOptionsRequestKey && !matchesSettledResult) {
      lastOptionsRequestKeyRef.current = requestKey;
      dispatch(fetchGlobalFilterOptions({ requestKey, payload }));
    }
  };

  const handlePlanToggle = (index) => {
    if (!open || !effectiveTenantValue?.code || !areTenantFiltersEnabled) {
      return;
    }

    setDidClearAll(false);
    preferCohortDraftSelectionRef.current = false;
    cohortDraftSelectionOverrideRef.current = null;
    cohortDraftForceSelectionRef.current = false;
    restoreBaseFiltersRef.current = null;
    freezeDivisionPlanHydrationRef.current = false;
    clearPersonIdValidationState({ preserveMessage: true });
    setIsPlanTouched(true);
    setCascadeMode('plan');
    optionAvailabilityModeRef.current = 'plan';

    const nextPlanValue = toggleSelectionOptions(planValue, index);
    setPlanValue(nextPlanValue);

    const selectedPlanLabels = nextPlanValue
      .slice(1)
      .filter((option) => option.checked)
      .map((option) => option.label);

    const hasPlanSelection = selectedPlanLabels.length > 0;

    const payload = {
      tenantCd: effectiveTenantValue.code,
      dateBasis: dateBasisValue,
      ...buildOptionsDatePayload(getEffectiveDateRangeValues()),
      ...(hasPlanSelection ? { plan: selectedPlanLabels } : {})
    };

    const requestKey = JSON.stringify(payload);
    const hasNewerRequestInFlight = Boolean(globalFilterOptionsRequestKey) && globalFilterOptionsRequestKey !== requestKey;
    const matchesSettledResult = requestKey === globalFilterOptionsLastSuccessKey && !hasNewerRequestInFlight;
    if (requestKey !== lastOptionsRequestKeyRef.current && requestKey !== globalFilterOptionsRequestKey && !matchesSettledResult) {
      lastOptionsRequestKeyRef.current = requestKey;
      dispatch(fetchGlobalFilterOptions({ requestKey, payload }));
    }
  };

  const handleDateFieldChange = (fieldName, nextValue, changeDetails = {}) => {
    clearPersonIdValidationState({ preserveMessage: true });
    hasDateDraftRef.current = true;

    const normalizedValue = (() => {
      const trimmed = String(nextValue ?? '').trim();
      const match = /^(\d{1,2})\/(\d{4})$/.exec(trimmed);
      if (!match) return nextValue;

      const month = Number(match[1]);
      const year = match[2];
      if (month < 1 || month > 12) return nextValue;

      return formatMonthYearValue(month, year);
    })();

    const currentValues = {
      currentPeriodFromValue,
      currentPeriodToValue,
      priorPeriodFromValue,
      priorPeriodToValue,
    };
    const nextDateOptionError = getDateOptionError(fieldName, normalizedValue, dateFieldOptionsByName[fieldName]);
    dateOptionErrorRef.current = nextDateOptionError;

    const fieldOptionValues = dateFieldOptionsByName[fieldName] || [];
    const isPredefinedAnchorField = fieldName === 'currentPeriodToValue'
      && (timeWindowValue === 'R12' || isPlanYtdTimeWindow(timeWindowValue));
    const isMatchedTypedDateOption = changeDetails.source === 'input'
      && fieldOptionValues.includes(normalizedValue);
    const shouldKeepPredefinedWindow = isPredefinedAnchorField
      && (changeDetails.source === 'option' || isMatchedTypedDateOption);

    const recalculatedPredefinedDates = shouldKeepPredefinedWindow
      ? predefinedCurrentPeriodDateRanges.find((dateRange) => dateRange.currentPeriodTo === normalizedValue) || null
      : null;

    setDateRangeValues(recalculatedPredefinedDates
      ? {
        currentPeriodFromValue: recalculatedPredefinedDates.currentPeriodFrom,
        currentPeriodToValue: recalculatedPredefinedDates.currentPeriodTo,
        priorPeriodFromValue: recalculatedPredefinedDates.priorPeriodFrom,
        priorPeriodToValue: recalculatedPredefinedDates.priorPeriodTo,
      }
      : { ...currentValues, [fieldName]: normalizedValue });

    setFieldErrors((currentValue) => ({
      ...currentValue,
      dateRange: nextDateOptionError ? DATE_OPTION_ERROR : null,
      dateRangeFields: nextDateOptionError
        ? { [nextDateOptionError.errorKey]: true }
        : {},
    }));
  };

  const handleTimeWindowChange = React.useCallback((event, selectedOption) => {
    const nextValue = selectedOption?.value;

    if (!nextValue) {
      return;
    }

    if (nextValue === 'R12' && !isRolling12Available) {
      return;
    }

    setTimeWindowValue(nextValue);
    clearPersonIdValidationState({ preserveMessage: true });
    ignoredTimeWindowDatesRef.current = timeWindowDates || null;
    dateOptionErrorRef.current = null;
    setFieldErrors((currentValue) => ({ ...currentValue, dateRange: null, dateRangeFields: {} }));

      if (nextValue === 'CUSTOM') {
        // Preserve valid prior dates when moving from a predefined window into Custom.
        // But if the predefined window left prior comparison logically enabled while the
        // prior dates are blank/sentinel, reset the toggle so Custom does not land in a
        // checked-but-empty state. That allows the user to turn the toggle back on and
        // auto-populate prior dates from the selected current period.
        const isSwitchingFromPredefinedWindow = Boolean(timeWindowValue) && timeWindowValue !== 'CUSTOM';
        const isShortHistoryCustomRange = !isRolling12Available;
        const hasDisabledPriorDateSentinel = priorPeriodFromValue === PRIOR_COMPARISON_DISABLED_DATE
          || priorPeriodToValue === PRIOR_COMPARISON_DISABLED_DATE;
        const hasUsablePriorDates = Boolean(
          String(priorPeriodFromValue || '').trim()
          && String(priorPeriodToValue || '').trim()
          && !hasDisabledPriorDateSentinel
        );

        if ((isSwitchingFromPredefinedWindow || isShortHistoryCustomRange) && !enablePriorComparison) {
          setPriorPeriodFromValue('');
          setPriorPeriodToValue('');
        }

        if (isSwitchingFromPredefinedWindow && enablePriorComparison && !hasUsablePriorDates) {
          setEnablePriorComparison(false);
          setPriorPeriodFromValue('');
          setPriorPeriodToValue('');
        }
    } else {
      hasDateDraftRef.current = false;
      shouldApplyHydratedDatesRef.current = true;
      const calculatedTimeWindowDates = calculateTimeWindowDates(nextValue, availableDates, planStartDate, planYearDateRanges, oldestAvailableDate);
      const defaultTimeWindowDates = nextValue === 'R12'
        ? normalizeRolling12DatesForAvailableHistory(calculatedTimeWindowDates, oldestAvailableDate)
        : calculatedTimeWindowDates;

      if (defaultTimeWindowDates) {
        setDateRangeValues({
          currentPeriodFromValue: defaultTimeWindowDates.currentPeriodFrom ?? defaultAppliedFilterValues.currentPeriodFrom,
          currentPeriodToValue: defaultTimeWindowDates.currentPeriodTo ?? defaultAppliedFilterValues.currentPeriodTo,
          priorPeriodFromValue: defaultTimeWindowDates.priorPeriodFrom ?? defaultAppliedFilterValues.priorPeriodFrom,
          priorPeriodToValue: defaultTimeWindowDates.priorPeriodTo ?? defaultAppliedFilterValues.priorPeriodTo,
        });
      } else {
        setDateRangeValues(emptyDateRangeValues);
      }
    }

  }, [
    availableDates,
    clearPersonIdValidationState,
    enablePriorComparison,
    isRolling12Available,
    oldestAvailableDate,
    planStartDate,
    planYearDateRanges,
    priorPeriodFromValue,
    priorPeriodToValue,
    setDateRangeValues,
    timeWindowDates,
    timeWindowValue,
  ]);

  React.useEffect(() => {
    // The first commit still holds the default time window; wait for the applied
    // filters to hydrate so a placeholder window never triggers a fallback.
    if (!isDrawerInitialized) {
      return;
    }

    const isSelectedTimeWindowAvailable = availableTimeWindowOptions.some(
      (option) => option.value === timeWindowValue
    );

    if (isSelectedTimeWindowAvailable) {
      return;
    }

    if (availableTimeWindowOptions.length > 0) {
      // handleTimeWindowChange already drops stale prior dates when falling back to
      // Custom Range on a short-history site.
      handleTimeWindowChange(null, availableTimeWindowOptions[0]);
      return;
    }

    setTimeWindowValue('');
    const minimumAvailableMonth = getMonthYearFromValue(oldestAvailableDate);
    const maximumAvailableMonth = getMonthYearFromValue(availableDates);
    setDateRangeValues({
      currentPeriodFromValue: minimumAvailableMonth
        ? formatMonthYearValue(minimumAvailableMonth.month, minimumAvailableMonth.year)
        : '',
      currentPeriodToValue: maximumAvailableMonth
        ? formatMonthYearValue(maximumAvailableMonth.month, maximumAvailableMonth.year)
        : '',
      priorPeriodFromValue: '',
      priorPeriodToValue: '',
    });
  }, [availableDates, availableTimeWindowOptions, handleTimeWindowChange, isDrawerInitialized, oldestAvailableDate, setDateRangeValues, timeWindowValue]);

  // No effect forces enablePriorComparison off when the prior period has no data.
  // The switch renders unchecked/disabled and Apply sends the disabled sentinel via
  // isPriorComparisonActive, while the user's preference is kept so the toggle comes
  // back on as soon as a window with real prior data is selected.

   const handlePriorComparisonChange = (event) => {
     const nextEnabled = event.target.checked;
     const hasDisabledDateSentinel = priorPeriodFromValue === PRIOR_COMPARISON_DISABLED_DATE
       || priorPeriodToValue === PRIOR_COMPARISON_DISABLED_DATE;
     let nextPriorPeriodFromValue = nextEnabled ? priorPeriodFromValue : '';
     let nextPriorPeriodToValue = nextEnabled ? priorPeriodToValue : '';

     if (
       nextEnabled
       && timeWindowValue === 'CUSTOM'
       && (hasDisabledDateSentinel || (!priorPeriodFromValue && !priorPeriodToValue))
     ) {
       // Auto-populate prior dates for Custom Range when toggling ON with no stored prior period.
       const calculatedPriorDates = calculateCustomRangePriorDates(
         currentPeriodFromValue,
         currentPeriodToValue,
         availableDates,
         oldestAvailableDate
       );
       nextPriorPeriodFromValue = calculatedPriorDates.priorPeriodFrom;
       nextPriorPeriodToValue = calculatedPriorDates.priorPeriodTo;
       setPriorPeriodFromValue(nextPriorPeriodFromValue);
       setPriorPeriodToValue(nextPriorPeriodToValue);
     } else if (nextEnabled && hasDisabledDateSentinel) {
       nextPriorPeriodFromValue = restorablePredefinedDates?.priorPeriodFrom || '';
       nextPriorPeriodToValue = restorablePredefinedDates?.priorPeriodTo || '';
       setPriorPeriodFromValue(nextPriorPeriodFromValue);
       setPriorPeriodToValue(nextPriorPeriodToValue);
     } else if (!nextEnabled && timeWindowValue === 'CUSTOM') {
       // Clear prior dates when toggling OFF in Custom Range mode
       setPriorPeriodFromValue('');
       setPriorPeriodToValue('');
     }

     setEnablePriorComparison(nextEnabled);
    clearPersonIdValidationState();

    const currentDateOptionError = dateOptionErrorRef.current;
    const shouldClearDateOptionError = !nextEnabled && isPriorPeriodDateFieldName(currentDateOptionError?.fieldName);
    if (shouldClearDateOptionError) {
      dateOptionErrorRef.current = null;
    }

    const nextDateValuesByFieldName = {
      currentPeriodFromValue,
      currentPeriodToValue,
      priorPeriodFromValue: nextPriorPeriodFromValue,
      priorPeriodToValue: nextPriorPeriodToValue,
    };
    const nextAllowEmptyPriorPeriod = !nextEnabled
      || allowEmptyRolling12PriorPeriod
      || (isPredefinedTimeWindow
        && isPlanYtdTimeWindow(timeWindowValue)
        && !String(priorPeriodFromValue || '').trim()
        && !String(priorPeriodToValue || '').trim());
    const { message: nextDateRangeError, invalidFields: nextInvalidDateRangeFields } = getDateRangeValidationResult(
      nextDateValuesByFieldName,
      { allowEmptyPriorPeriod: nextAllowEmptyPriorPeriod }
    );

    const nextDateOptionError = dateOptionErrorRef.current;
    const hasNextDateOptionError = Boolean(
      nextDateOptionError
      && (!isPriorPeriodDateFieldName(nextDateOptionError.fieldName) || nextEnabled)
      && nextDateValuesByFieldName[nextDateOptionError.fieldName] === nextDateOptionError.value
    );

    setFieldErrors((currentValue) => ({
      ...currentValue,
      dateRange: nextDateRangeError || (hasNextDateOptionError ? DATE_OPTION_ERROR : null),
      dateRangeFields: nextDateRangeError
        ? nextInvalidDateRangeFields
        : hasNextDateOptionError
          ? { [nextDateOptionError.errorKey]: true }
          : {},
    }));
  };

  const handleTenantChange = (event, value) => {
    lastOptionsRequestKeyRef.current = '';
    setHasTenantDraftSelection(true);
    setFieldErrors({});
    clearPersonIdValidationState();
    setDivisionValue(divisionOptions.map((option) => ({ ...option })));
    setPlanValue(planOptions.map((option) => ({ ...option })));
    setDivisionSearchQuery('');
    setPlanSearchQuery('');
    setIsDivisionTouched(false);
    setIsPlanTouched(false);
    setCascadeMode(null);
    setPreviewedSelection(null);
    preferCohortDraftSelectionRef.current = false;
    cohortDraftSelectionOverrideRef.current = null;
    cohortDraftForceSelectionRef.current = false;
    restoreBaseFiltersRef.current = null;
    freezeDivisionPlanHydrationRef.current = false;
    optionAvailabilityModeRef.current = null;
    clearAllRequestKeyRef.current = null;
    setDidClearAll(false);
    pendingUnfilteredOptionsRequestKeyRef.current = null;
    lastHydratedOptionsKeyRef.current = null;
    lastHydratedBaselineKeyRef.current = null;
    hasDateDraftRef.current = false;
    shouldApplyHydratedDatesRef.current = true;
    ignoredTimeWindowDatesRef.current = timeWindowDates || null;
    setOptionsBaselineCache({});

    dispatch(resetGlobalFilterOptions());
    setDateBasisValue(defaultAppliedFilterValues.dateBasis);
    setDateRangeValues(emptyDateRangeValues);
    setPersonIdValue(defaultAppliedFilterValues.personIdValue);
    setCohortValue(defaultAppliedFilterValues.cohortLuid);
    setPopulationMode(defaultAppliedFilterValues.populationMode);
    latestCohortRequestIdRef.current += 1;
    pendingCohortLuidRef.current = null;
    shouldRefreshCohortPersonIdsRef.current = false;
    setIsCohortPersonIdsLoading(false);
    setTimeWindowValue(defaultAppliedFilterValues.timeWindow);
    setTenantValue(value);
    if (value?.code && value?.filtersEnabled !== false) {
      // Preview the tenant's saved filter into the drawer (draft only; committed on Apply).
      previewSavedFilter(value.code);
    } else {
      latestPreviewRequestIdRef.current += 1;
      setEnablePriorComparison(defaultAppliedFilterValues.enablePriorComparison);
      setIsSavedFilterPreviewLoading(false);
    }
  };

  const handleDateBasisChange = (value) => {
    clearPersonIdValidationState({ preserveMessage: true });
    hasDateDraftRef.current = false;
    shouldApplyHydratedDatesRef.current = true;
    ignoredTimeWindowDatesRef.current = timeWindowDates || null;
    dateOptionErrorRef.current = null;
    setDateBasisValue(value);

    if (timeWindowValue !== 'CUSTOM') {
      setDateRangeValues(emptyDateRangeValues);
    }
  };

  const handleClearAll = () => {
    setDidClearAll(true);
    preferCohortDraftSelectionRef.current = false;
    cohortDraftSelectionOverrideRef.current = null;
    cohortDraftForceSelectionRef.current = false;
    restoreBaseFiltersRef.current = null;
    freezeDivisionPlanHydrationRef.current = false;
    clearPersonIdValidationState();

    suppressNextOptionsFetchRef.current = true;
    lastOptionsRequestKeyRef.current = '';
    hasDateDraftRef.current = false;
    shouldApplyHydratedDatesRef.current = true;
    dateOptionErrorRef.current = null;
    clearPersonIdValidationState();

    const calculatedClearAllDates = hasAvailableDateBounds
      ? calculateTimeWindowDates(defaultAppliedFilterValues.timeWindow, availableDates, planStartDate, planYearDateRanges, oldestAvailableDate)
      : null;
    const normalizedClearAllDates = defaultAppliedFilterValues.timeWindow === 'R12'
      ? normalizeRolling12DatesForAvailableHistory(calculatedClearAllDates, oldestAvailableDate)
      : calculatedClearAllDates;
    const clearAllDateRangeValues = normalizedClearAllDates
      ? toDateRangeValues(normalizedClearAllDates)
      : emptyDateRangeValues;

    // Reset local UI state; Division/Plan option lists will be re-hydrated from the Clear All options API response.
    setDivisionValue(divisionOptions.map((option) => ({ ...option })));
    setPlanValue(planOptions.map((option) => ({ ...option })));
    setIsDivisionTouched(true);
    setIsPlanTouched(true);
    setCascadeMode(null);
    optionAvailabilityModeRef.current = null;
    // Clear hydration keys to force fresh re-hydration after Clear All
    lastHydratedOptionsKeyRef.current = null;
    lastHydratedBaselineKeyRef.current = null;
    setPreviewedSelection(null);
    setDateBasisValue(defaultAppliedFilterValues.dateBasis);
    setCurrentPeriodFromValue(clearAllDateRangeValues.currentPeriodFromValue);
    setCurrentPeriodToValue(clearAllDateRangeValues.currentPeriodToValue);
    latestCohortRequestIdRef.current += 1;
    pendingCohortLuidRef.current = null;
    shouldRefreshCohortPersonIdsRef.current = false;
    setIsCohortPersonIdsLoading(false);
    setPersonIdValue(defaultAppliedFilterValues.personIdValue);
    setPriorPeriodFromValue(clearAllDateRangeValues.priorPeriodFromValue);
    setPriorPeriodToValue(clearAllDateRangeValues.priorPeriodToValue);
    setEnablePriorComparison(defaultAppliedFilterValues.enablePriorComparison);
    setTimeWindowValue(defaultAppliedFilterValues.timeWindow);
    setCohortValue(defaultAppliedFilterValues.cohortLuid);
    setPopulationMode(defaultAppliedFilterValues.populationMode);
    setFieldErrors({});
    setDivisionSearchQuery('');
    setPlanSearchQuery('');

    if (open && effectiveTenantValue?.code && areTenantFiltersEnabled) {
      dispatch(fetchAvailableDateBoundsAndPlanYear(
        defaultAppliedFilterValues.dateBasis,
        defaultAppliedFilterValues.timeWindow,
        effectiveTenantValue.code,
        { hideSpinner: true }
      ));

      const currentStart = monthYearToMonthStart(clearAllDateRangeValues.currentPeriodFromValue);
      const currentEnd = monthYearToMonthStart(clearAllDateRangeValues.currentPeriodToValue);
      const priorStart = monthYearToMonthStart(clearAllDateRangeValues.priorPeriodFromValue);
      const priorEnd = monthYearToMonthStart(clearAllDateRangeValues.priorPeriodToValue);

      const payload = {
        tenantCd: effectiveTenantValue.code,
        dateBasis: defaultAppliedFilterValues.dateBasis,
        ...(currentStart ? { currentStart } : {}),
        ...(currentEnd ? { currentEnd } : {}),
        ...(priorStart ? { priorStart } : {}),
        ...(priorEnd ? { priorEnd } : {})
        // Intentionally omit division/plan from Clear All fetch.
      };

      const requestKey = JSON.stringify(payload);
      clearAllRequestKeyRef.current = requestKey;
      const isSkippedClearAll = requestKey !== lastOptionsRequestKeyRef.current && requestKey !== globalFilterOptionsRequestKey && requestKey !== globalFilterOptionsLastSuccessKey;
      if (isSkippedClearAll) {
        lastOptionsRequestKeyRef.current = requestKey;
        dispatch(fetchGlobalFilterOptions({ requestKey, payload }));
      } else {
        // Fetch skipped — request is already in-flight, or options already match the last successful response.
        // Reset the hydration key so the effect re-hydrates divisions/plans
        // with all options checked on a repeated Clear All.
        lastHydratedOptionsKeyRef.current = null;
      }
    }

    // Panel stays open after Clear All; filters are NOT applied until the user clicks Apply.
  };

  const [isReallyLoading, setIsReallyLoading] = useState(false);
  const isAnyLoading = isCohortPersonIdsLoading || isGlobalFilterOptionsLoading || isPersonIdValidationLoading;
  // Clear All must stay enabled during Person ID validation; it cancels any in-flight validation and resets fields.
  const isClearAllDisabled = isCohortPersonIdsLoading || isGlobalFilterOptionsLoading;

  useEffect(() => {
    if(isAnyLoading) {
      setIsReallyLoading(true);
      return;
    }

    const timer = setTimeout(() => {
      setIsReallyLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [isAnyLoading]);

  const handleApply = () => {
    if ((isCohortBuilderEnabled && (isCohortPersonIdsLoading || isPersonIdValidationLoading)) || isGlobalFilterOptionsLoading) {
      return;
    }

    if (!areTenantFiltersEnabled) {
      hasDateDraftRef.current = false;
      onApply?.({
        tenant: effectiveTenantValue,
        dateBasis: defaultAppliedFilterValues.dateBasis,
        timeWindow: defaultAppliedFilterValues.timeWindow,
        currentPeriodFrom: timeWindowDates?.currentPeriodFrom || appliedFilters?.currentPeriodFrom || defaultAppliedFilterValues.currentPeriodFrom,
        currentPeriodTo: timeWindowDates?.currentPeriodTo || appliedFilters?.currentPeriodTo || defaultAppliedFilterValues.currentPeriodTo,
        priorPeriodFrom: timeWindowDates?.priorPeriodFrom || appliedFilters?.priorPeriodFrom || defaultAppliedFilterValues.priorPeriodFrom,
        priorPeriodTo: timeWindowDates?.priorPeriodTo || appliedFilters?.priorPeriodTo || defaultAppliedFilterValues.priorPeriodTo,
        enablePriorComparison: defaultAppliedFilterValues.enablePriorComparison,
        divisionLabels: [],
        planLabels: [],
        isAllDivisionsSelected: true,
        isAllPlansSelected: true,
        divisionsConstrainedByPlan: false,
        plansConstrainedByDivision: false,
        personIdValue: defaultAppliedFilterValues.personIdValue,
        populationMode: defaultAppliedFilterValues.populationMode,
        cohortLuid: defaultAppliedFilterValues.cohortLuid,
        cohortName: defaultAppliedFilterValues.cohortName,
      });
      return;
    }

    const nextErrors = {};
    const selectedDivisionLabels = areDivisionsEnabled ? divisionValue
      .slice(1)
      .filter((option) => option.checked)
      .map((option) => option.label) : [];
    const selectedPlanLabels = arePlansEnabled ? planValue
      .slice(1)
      .filter((option) => option.checked)
      .map((option) => option.label) : [];
    const enabledDivisionCount = divisionValue.slice(1).filter((option) => !option.disabled).length;
    const enabledPlanCount = planValue.slice(1).filter((option) => !option.disabled).length;
    const isAllDivisionsSelected = !areDivisionsEnabled || selectedDivisionLabels.length === enabledDivisionCount;
    const isAllPlansSelected = !arePlansEnabled || selectedPlanLabels.length === enabledPlanCount;

    // A division/plan set is "constrained" when the counterpart selection has
    // narrowed its available options below the tenant's full (unfiltered)
    // universe. The banner uses these flags to avoid labeling a narrowed set as
    // "All Divisions"/"All Plans".
    const optionsBaseRequestKey = JSON.stringify(buildOptionsBasePayload({
      tenantCd: effectiveTenantValue?.code,
      dateBasisValue,
      ...getEffectiveDateRangeValues(),
    }));
    const baselineUniverseOptions = optionsBaselineCache[optionsBaseRequestKey] ?? emptyGlobalFilterOptions;
    const fullDivisionUniverseCount = Array.isArray(baselineUniverseOptions.division)
      ? baselineUniverseOptions.division.length
      : 0;
    const fullPlanUniverseCount = Array.isArray(baselineUniverseOptions.plan)
      ? baselineUniverseOptions.plan.length
      : 0;
    const computedDivisionsConstrainedByPlan = fullDivisionUniverseCount > 0 && enabledDivisionCount < fullDivisionUniverseCount;
    const computedPlansConstrainedByDivision = fullPlanUniverseCount > 0 && enabledPlanCount < fullPlanUniverseCount;
    const divisionsConstrainedByPlan = areDivisionsEnabled && arePlansEnabled && computedDivisionsConstrainedByPlan;
    const plansConstrainedByDivision = areDivisionsEnabled && arePlansEnabled && computedPlansConstrainedByDivision;

    if (showTenantFilter && !effectiveTenantValue) {
      nextErrors.tenant = 'Field cannot be empty.';
    }

    if (isCohortBuilderEnabled && personIdValue.length > PERSON_ID_MAX_LENGTH) {
      nextErrors.personId = PERSON_ID_CHARACTER_LIMIT_ERROR;
    } else if (isCohortBuilderEnabled && !isValidPersonIdValue(personIdValue)) {
      nextErrors.personId = PERSON_ID_FORMAT_ERROR;
    }

    if (areDivisionsEnabled && !hasAnyNonAllOptionChecked(divisionValue)) {
      nextErrors.division = 'Select at least one Division.';
    }

    if (arePlansEnabled && !hasAnyNonAllOptionChecked(planValue)) {
      nextErrors.plan = 'Select at least one Plan.';
    }

    const effectiveDateRangeValues = getEffectiveDateRangeValues();
    const currentDateOptionError = dateOptionErrorRef.current;
    // Use raw state values for option-error matching and R12 format detection.
    // getEffectiveDateRangeValues() normalizes R12 dates (recalculates To from From),
    // which would mask an invalid typed To and let Apply proceed with the auto-calculated date.
    const rawDateValues = {
      currentPeriodFromValue,
      currentPeriodToValue,
      priorPeriodFromValue,
      priorPeriodToValue,
    };
    const hasCurrentDateOptionError = Boolean(
      currentDateOptionError
      && rawDateValues[currentDateOptionError.fieldName] === currentDateOptionError.value
    );
    // Note: isValidMonthValue('') returns false, so this also catches cleared/empty values.
    const trimmedCurrentPeriodToValue = String(currentPeriodToValue || '').trim();
    const hasInvalidRawR12To = timeWindowValue === 'R12' && !isValidMonthValue(trimmedCurrentPeriodToValue);
    const { message: dateRangeError, invalidFields: invalidDateRangeFields } = hasInvalidRawR12To
      ? { message: FORMAT_ERROR, invalidFields: { currentPeriodTo: true } }
      : validateDateRange();
    if (dateRangeError) {
      nextErrors.dateRange = dateRangeError;
      nextErrors.dateRangeFields = invalidDateRangeFields;
    } else if (hasCurrentDateOptionError) {
      nextErrors.dateRange = DATE_OPTION_ERROR;
      nextErrors.dateRangeFields = { [currentDateOptionError.errorKey]: true };
    }

    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const finalizeApply = (sanitizedPersonIdValue) => {
      hasDateDraftRef.current = false;

      const allCohortOptions = Object.values(cohortOptions || {}).flat();
      const selectedCohortOption = allCohortOptions.find((option) => option?.value === cohortValue);
      const hasAppliedPersonIds = Boolean(String(sanitizedPersonIdValue || '').trim());
      const effectivePopulationMode = isCohortBuilderEnabled && hasAppliedPersonIds ? populationMode : null;
      const selectedCohortName =
        effectivePopulationMode === POPULATION_MODE.COHORT
          ? selectedCohortOption?.label || appliedFilters?.cohortName || ''
          : '';
      const selectedCohortLuid =
        effectivePopulationMode === POPULATION_MODE.COHORT
          ? cohortValue || appliedFilters?.cohortLuid || ''
          : '';
      const hasAvailableTimeWindow = availableTimeWindowOptions.length > 0;
      const minimumAvailableMonth = getMonthYearFromValue(oldestAvailableDate);
      const maximumAvailableMonth = getMonthYearFromValue(availableDates);
      const fullRangeFrom = minimumAvailableMonth
        ? formatMonthYearValue(minimumAvailableMonth.month, minimumAvailableMonth.year)
        : '';
      const fullRangeTo = maximumAvailableMonth
        ? formatMonthYearValue(maximumAvailableMonth.month, maximumAvailableMonth.year)
        : '';

      onApply?.({
        tenant: effectiveTenantValue,
        dateBasis: dateBasisValue,
        timeWindow: hasAvailableTimeWindow ? timeWindowValue : '',
        currentPeriodFrom: hasAvailableTimeWindow ? effectiveDateRangeValues.currentPeriodFromValue : fullRangeFrom,
        currentPeriodTo: hasAvailableTimeWindow ? effectiveDateRangeValues.currentPeriodToValue : fullRangeTo,
        priorPeriodFrom: hasAvailableTimeWindow
          ? (isPriorComparisonActive ? priorPeriodFromValue : PRIOR_COMPARISON_DISABLED_DATE)
          : '',
        priorPeriodTo: hasAvailableTimeWindow
          ? (isPriorComparisonActive ? priorPeriodToValue : PRIOR_COMPARISON_DISABLED_DATE)
          : '',
        enablePriorComparison: hasAvailableTimeWindow && isPriorComparisonActive,
        divisionLabels: selectedDivisionLabels,
        planLabels: selectedPlanLabels,
        isAllDivisionsSelected,
        isAllPlansSelected,
        divisionsConstrainedByPlan,
        plansConstrainedByDivision,
        personIdValue: isCohortBuilderEnabled ? sanitizedPersonIdValue : '',
        populationMode: effectivePopulationMode,
        cohortLuid: selectedCohortLuid,
        cohortName: selectedCohortName,
      });
    };

    const shouldValidatePersonIdsOnApply = isCohortBuilderEnabled
      && (populationMode === POPULATION_MODE.PERSON_ID || populationMode === POPULATION_MODE.COHORT)
      && Boolean(String(personIdValue || '').trim());

    if (!shouldValidatePersonIdsOnApply) {
      finalizeApply(personIdValue);
      return;
    }

    Promise.resolve(validatePersonIdsAgainstContext())
      .then((personIdValidationResult) => {
        if (!personIdValidationResult?.ok) {
          return;
        }
        finalizeApply(personIdValidationResult.sanitizedPersonIds);
      })
      .catch(() => {
        // Validation errors are already reflected in component state.
      });
  };

  const handleResetDivisionPlanSelections = () => {
    setDidClearAll(false);
    preferCohortDraftSelectionRef.current = false;
    cohortDraftSelectionOverrideRef.current = null;
    cohortDraftForceSelectionRef.current = false;
    restoreBaseFiltersRef.current = null;
    freezeDivisionPlanHydrationRef.current = false;
    setIsDivisionTouched(true);
    setIsPlanTouched(true);
    setCascadeMode(null);
    optionAvailabilityModeRef.current = null;
    setDivisionValue((currentValue) => buildAllCheckedFromCurrentOptions(currentValue));
    setPlanValue((currentValue) => buildAllCheckedFromCurrentOptions(currentValue));
    setFieldErrors((currentValue) => ({ ...currentValue, division: null, plan: null }));

    if (!open || !effectiveTenantValue?.code || !areTenantFiltersEnabled) {
      return;
    }

    const payload = {
      tenantCd: effectiveTenantValue.code,
      dateBasis: dateBasisValue,
      ...buildOptionsDatePayload(getEffectiveDateRangeValues())
    };

    const requestKey = JSON.stringify(payload);
    if (requestKey !== lastOptionsRequestKeyRef.current && requestKey !== globalFilterOptionsRequestKey && requestKey !== globalFilterOptionsLastSuccessKey) {
      lastOptionsRequestKeyRef.current = requestKey;
      resetDivisionPlanRequestKeyRef.current = requestKey;
      dispatch(fetchGlobalFilterOptions({ requestKey, payload }));
    }
  };

  const shouldShowResetDivisionPlanAction = hasAnyNonAllOptionUnchecked(divisionValue) || hasAnyNonAllOptionUnchecked(planValue);
  const shouldShowDivisionLoadingPanel = Boolean(isGlobalFilterOptionsLoading) && divisionValue.length <= 1;
  const shouldShowPlanLoadingPanel = Boolean(isGlobalFilterOptionsLoading) && planValue.length <= 1;
  const hasNoSelectedDivision = areTenantFiltersEnabled && areDivisionsEnabled && !hasAnyNonAllOptionChecked(divisionValue);
  const hasNoSelectedPlan = areTenantFiltersEnabled && arePlansEnabled && !hasAnyNonAllOptionChecked(planValue);

  const selectedTimeWindowOption = availableTimeWindowOptions.find((option) => option.value === timeWindowValue)
    || availableTimeWindowOptions[0]
    || null;
  const handleDrawerClose = (event, reason) => {
    onClose?.(event, reason);
  };

  React.useEffect(() => {
    if (open && filtersScrollRef.current) {
      filtersScrollRef.current.scrollTop = 0;
    }
  }, [open]);

  const resetDivisionPlanAction = shouldShowResetDivisionPlanAction ? (
    <Button
      variant="outlined"
      onClick={handleResetDivisionPlanSelections}
      sx={getResetButtonSx(true)}
    >
      Reset
    </Button>
  ) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      ModalProps={{ keepMounted: true }}
      BackdropProps={{
        sx: {
          backgroundColor: colors.backdrop
        }
      }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 672 },
          maxWidth: '100%',
          height: '100%',
          borderRadius: 0,
          backgroundColor: '#FFFFFF'
        }
      }}>
      <Box display="flex" flexDirection="column" height="100%" bgcolor="#FFFFFF">
        <Box px={3} py={3} borderBottom={`${drawerDividerWidth} solid ${drawerDividerColor}`}>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <IconButton aria-label="Close global filters" onClick={(event) => handleDrawerClose(event, 'close-icon')} sx={{p: 0}}>
              <CloseIcon sx={{ fontSize: 24, color: colors.text }} />
            </IconButton>
          </Box>
        </Box>

        <Box ref={filtersScrollRef} data-testid="global-filters-scroll-container" flex={1} overflow="auto" px={3} pt={3} pb={5}>
          <Box display="flex" flexDirection="column" gap='28px'>
            {areTenantFiltersEnabled ? (
              <Box display="flex" flexDirection="column" gap='15px'>
                <Box display="flex" flexDirection="column" gap='20px'>
                  <Typography
                    sx={{
                      fontFamily: 'Rubik',
                      fontWeight: 600,
                      fontSize: 20,
                      lineHeight: '30px',
                      marginBottom: '-5px',
                      color: colors.text
                    }}>
                    Filters
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Noto Sans',
                      fontWeight: 400,
                      fontSize: 16,
                      lineHeight: '24px',
                      marginBlock: '-4px',
                      color: colors.text
                    }}>
                    Set filters once and apply them across BenInsights.
                  </Typography>
                </Box>

                <Typography sx={{ ...helperTextSx, fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Note:{' '}
                  </Box>
                  Filters do not apply to My Creations and some filters may not apply to all reports.
                </Typography>
              </Box>
            ) : null}

            <Box display="flex" flexDirection="column" gap={3.5}>
              {showTenantFilter ? (
                <FilterSection label="Tenant" helperText="Search by tenant name or ID." helperPlacement="above">
                  <Box display="flex" flexDirection="column" gap={1} width="100%">
                    <Autocomplete
                      disableClearable
                      open={isTenantOpen}
                      options={companies || []}
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) => option?.code === value?.code}
                      onChange={handleTenantChange}
                      onClose={() => setIsTenantOpen(false)}
                      onOpen={() => setIsTenantOpen(true)}
                      value={effectiveTenantValue}
                      filterOptions={filterOptions}
                      popupIcon={<IconChevronDown stroke={2} size={20} color={colors.iconFill} />}
                      slotProps={{
                        paper: { sx: getDropdownPaperSx(isTenantOpen, colors.primary) },
                        popper: { className: 'bst-autocomplete-popper', disablePortal: true }
                      }}
                      renderOption={(renderProps, option, { selected }) => {
                        const { className, key, ...optionProps } = renderProps;

                        return (
                          <Box component="li" key={key ?? option?.id ?? option?.code ?? option?.name} {...optionProps} sx={getDropdownOptionSx(selected)}>
                            {option?.name || ''}
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder=""
                          sx={fieldErrors.tenant 
                            ? getErrorFieldSx(getDropdownFieldSx(isTenantOpen ? colors.primary : colors.border)) 
                            : getDropdownFieldSx(isTenantOpen ? colors.primary : colors.border)}
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': 'Tenant Name/ID'
                          }}
                        />
                      )}
                    />
                    {fieldErrors.tenant ? <Typography sx={errorTextSx}>{fieldErrors.tenant}</Typography> : null}
                  </Box>
                </FilterSection>
              ) : null}

              {areTenantFiltersEnabled ? (
                <>
                  {isDateBasisEnabled ? (
                  <FilterSection label="Date Basis" helperText="Determines which datasets and time calculations power metrics." helperPlacement="above">
                    <Box display="flex" flexDirection="column" gap={1} width="100%">
                      <Box data-testid="date-basis-selection-panel" sx={{ ...selectionPanelSx, maxHeight: 'none' }}>
                        <Box display="flex" flexDirection="column" gap={3}>
                          {dateBasisOptions.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              control={<Radio checked={dateBasisValue === option.value} onChange={() => handleDateBasisChange(option.value)} sx={radioSx} />}
                              label={<Typography sx={optionLabelSx}>{option.label}</Typography>}
                              sx={{ ...radioOptionRowSx, m: 0, gap: 1 }}
                            />
                          ))}
                        </Box>
                      </Box>
                      {dateBasisValue === 'incurred' ? <Typography sx={dateBasisGuidanceTextSx}>{INCURRED_DATE_BASIS_GUIDANCE}</Typography> : null}
                    </Box>
                  </FilterSection>
                  ) : null}

                  {isDateRangeEnabled ? (
                  <FilterSection label="Date Range" helperText="Select a time window or choose Custom Range to edit every date." helperPlacement="above">
                    <Box display="flex" flexDirection="column" gap={1} width="100%">
                      <Box sx={getDateRangePanelSx(Boolean(fieldErrors.dateRange))}>
                        {availableTimeWindowOptions.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={2} width="100%">
                          <Typography sx={optionLabelSx}>Time Window</Typography>
                          <Autocomplete
                            disabled={availableTimeWindowOptions.length === 0}
                            disableClearable
                            disablePortal
                            open={isTimeWindowOpen}
                            options={availableTimeWindowOptions}
                            value={selectedTimeWindowOption}
                            getOptionLabel={(option) => option?.label || ''}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            onChange={handleTimeWindowChange}
                            onClose={() => setIsTimeWindowOpen(false)}
                            onOpen={() => setIsTimeWindowOpen(true)}
                            fullWidth
                            popupIcon={<IconChevronDown stroke={2} size={20} color={colors.iconFill} />}
                            slotProps={{
                              paper: { sx: getTimeWindowMenuProps(isTimeWindowOpen).PaperProps.sx },
                              listbox: { sx: getTimeWindowMenuProps(isTimeWindowOpen).MenuListProps.sx },
                            }}
                            renderOption={(renderProps, option) => {
                              const { className, key, ...optionProps } = renderProps;
                              const selected = timeWindowValue === option.value;
                              const index = availableTimeWindowOptions.findIndex((timeWindowOption) => timeWindowOption.value === option.value);

                              return (
                                <MenuItem
                                  component="li"
                                  disableGutters
                                  disableRipple
                                  key={key ?? option.value}
                                  selected={selected}
                                  {...optionProps}
                                  sx={getTimeWindowMenuItemSx(selected, index, availableTimeWindowOptions.length)}>
                                  {option.label}
                                </MenuItem>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                sx={getTimeWindowSelectSx()}
                                inputProps={{
                                  ...params.inputProps,
                                  'aria-label': 'Time Window'
                                }}
                              />
                            )}
                          />
                        </Box>
                        ) : null}

                        <Box display="flex" flexDirection="column" gap={1} width="100%">
                          <Typography sx={optionLabelSx}>{`Current Period (${dateBasisValue === 'paid' ? 'Paid' : 'Incurred'})`}</Typography>
                          <Box display="flex" gap={3} width="100%">
                            <Box display="flex" flexDirection="column" gap={1} width="50%">
                              <Typography sx={dateInputLabelSx}>From</Typography>
                              <DateFieldSelector
                                ariaLabel="Current Period From"
                                value={currentPeriodFromValue}
                                onBlur={validateDateRangeOnBlur}
                                onChange={(value, changeDetails) => handleDateFieldChange('currentPeriodFromValue', value, changeDetails)}
                                onEditingChange={setIsDateRangeEditing}
                                isLocked={isPredefinedTimeWindow || availableTimeWindowOptions.length === 0}
                                lockMessage={LOCKED_DATE_FIELD_MESSAGE}
                                hasError={Boolean(fieldErrors.dateRangeFields?.currentPeriodFrom)}
                                optionValues={currentPeriodFromOptions}
                              />
                            </Box>
                            <Box display="flex" flexDirection="column" gap={1} width="50%">
                              <Typography sx={dateInputLabelSx}>To</Typography>
                              <DateFieldSelector
                                ariaLabel="Current Period To"
                                value={currentPeriodToValue}
                                onBlur={validateDateRangeOnBlur}
                                onChange={(value, changeDetails) => handleDateFieldChange('currentPeriodToValue', value, changeDetails)}
                                onEditingChange={setIsDateRangeEditing}
                                isLocked={availableTimeWindowOptions.length === 0}
                                lockMessage={LOCKED_DATE_FIELD_MESSAGE}
                                hasError={Boolean(fieldErrors.dateRangeFields?.currentPeriodTo)}
                                optionValues={currentPeriodToOptions}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {availableTimeWindowOptions.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={1} width="100%">
                          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                            <Typography sx={optionLabelSx}>{`Prior Period (${dateBasisValue === 'paid' ? 'Paid' : 'Incurred'})`}</Typography>
                            {isSavedFilterPreviewLoading ? (
                              <CircularProgress
                                aria-label="Prior comparison setting loading"
                                size={20}
                                thickness={4}
                                sx={{ color: colors.primary }}
                              />
                            ) : (
                              <Switch
                                checked={isPriorComparisonAvailable ? enablePriorComparison : false}
                                onChange={handlePriorComparisonChange}
                                disabled={!isPriorComparisonAvailable}
                                inputProps={{ 'aria-label': 'Include prior period comparison' }}
                                sx={priorComparisonSwitchSx}
                              />
                            )}
                          </Box>
                          {isSavedFilterPreviewLoading ? null : isPriorComparisonAvailable ? (
                            <Typography sx={priorComparisonHelperTextSx}>
                              {PRIOR_COMPARISON_GUIDANCE_TEXT}
                            </Typography>
                          ) : shouldShowPriorComparisonUnavailable ? (
                            <Typography sx={priorComparisonHelperTextSx}>
                              {PRIOR_COMPARISON_UNAVAILABLE_TEXT}
                            </Typography>
                          ) : null}
                          {isPriorComparisonActive && !isSavedFilterPreviewLoading ? (
                            <Box display="flex" gap={3} width="100%">
                              <Box display="flex" flexDirection="column" gap={1} width="50%">
                                <Typography sx={dateInputLabelSx}>From</Typography>
                                <DateFieldSelector
                                  ariaLabel="Prior Period From"
                                  value={priorPeriodFromValue}
                                  onBlur={validateDateRangeOnBlur}
                                  onChange={(value, changeDetails) => handleDateFieldChange('priorPeriodFromValue', value, changeDetails)}
                                  onEditingChange={setIsDateRangeEditing}
                                  isLocked={isPredefinedTimeWindow}
                                  lockMessage={LOCKED_DATE_FIELD_MESSAGE}
                                  hasError={Boolean(fieldErrors.dateRangeFields?.priorPeriodFrom)}
                                  optionValues={priorPeriodFromOptions}
                                />
                              </Box>
                              <Box display="flex" flexDirection="column" gap={1} width="50%">
                                <Typography sx={dateInputLabelSx}>To</Typography>
                                <DateFieldSelector
                                  ariaLabel="Prior Period To"
                                  value={priorPeriodToValue}
                                  onBlur={validateDateRangeOnBlur}
                                  onChange={(value, changeDetails) => handleDateFieldChange('priorPeriodToValue', value, changeDetails)}
                                  onEditingChange={setIsDateRangeEditing}
                                  isLocked={isPredefinedTimeWindow}
                                  lockMessage={LOCKED_DATE_FIELD_MESSAGE}
                                  hasError={Boolean(fieldErrors.dateRangeFields?.priorPeriodTo)}
                                  optionValues={priorPeriodToOptions}
                                />
                              </Box>
                            </Box>
                          ) : null}
                          {shouldShowRolling12PriorPeriodWarning && !isSavedFilterPreviewLoading ? (
                            <Box display="flex" alignItems="center" gap={0.5} width="100%" data-testid="r12-prior-period-warning">
                              <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: colors.text }} />
                              <Typography sx={warningTextSx}>{R12_PRIOR_PERIOD_WARNING_TEXT}</Typography>
                            </Box>
                          ) : null}
                        </Box>
                        ) : null}
                      </Box>
                      {fieldErrors.dateRange ? <Typography sx={errorTextSx}>{fieldErrors.dateRange}</Typography> : null}
                    </Box>
                  </FilterSection>
                  ) : null}

                  {areDivisionsEnabled || arePlansEnabled ? (
                  <FilterSection action={resetDivisionPlanAction} label="Divisions & Plans" helperText={DIVISIONS_PLANS_HELPER_TEXT} helperPlacement="above">
                    <Box display="flex" flexDirection="column" gap={2} width="100%">
                      {areDivisionsEnabled ? (
                      <Box data-testid="division-selection-panel">
                        {shouldShowDivisionLoadingPanel ? (
                          <LoadingPanel />
                        ) : (
                          <CheckboxList
                            errorText={fieldErrors.division}
                            options={divisionValue}
                            onToggle={handleDivisionToggle}
                            searchQuery={divisionSearchQuery}
                            onSearchQueryChange={setDivisionSearchQuery}
                            sectionLabel="Divisions"
                            selectedSummaryLabel="Selected Divisions"
                            showFirstItemSeparator
                            unavailableWarningText={DIVISION_UNAVAILABLE_WARNING_TEXT}
                            isRefreshing={Boolean(isGlobalFilterOptionsLoading) && cascadeMode !== 'division'}
                          />
                        )}
                      </Box>
                      ) : null}

                      {arePlansEnabled ? (
                      <Box data-testid="plan-selection-panel">
                        {shouldShowPlanLoadingPanel ? (
                          <LoadingPanel />
                        ) : (
                          <CheckboxList
                            errorText={fieldErrors.plan}
                            options={planValue}
                            onToggle={handlePlanToggle}
                            searchQuery={planSearchQuery}
                            onSearchQueryChange={setPlanSearchQuery}
                            sectionLabel="Plans"
                            selectedSummaryLabel="Selected Plans"
                            showFirstItemSeparator
                            unavailableWarningText={PLAN_UNAVAILABLE_WARNING_TEXT}
                            isRefreshing={Boolean(isGlobalFilterOptionsLoading) && cascadeMode !== 'plan'}
                          />
                        )}
                      </Box>
                      ) : null}
                    </Box>
                  </FilterSection>
                  ) : null}

                  {isCohortBuilderEnabled ? (
                    <PopulationFilters
                    open={open}
                    effectiveTenantValue={effectiveTenantValue?.code}
                    cohortValue={cohortValue}
                    cohortDisplayName={appliedFilters?.cohortName}
                    onCohortChange={handleCohortSelectionChange}
                    personIdValue={personIdValue}
                    personIdError={fieldErrors.personId}
                    personIdWarningMessage={personIdValidationMessage}
                    personIdValidationLoading={isPersonIdValidationLoading}
                    onPersonIdChange={handlePersonIdChange}
                    onPersonIdBlur={handlePersonIdBlur}
                    onPersonIdReset={handlePersonIdReset}
                    onPopulationReset={handlePopulationReset}
                    populationMode={populationMode}
                    onPopulationModeChange={handlePopulationModeChange}
                    cohortPersonIdsLoading={isCohortPersonIdsLoading}
                    onClose={handleDrawerClose}
                    onOpenCohortBuilder={handleOpenCohortBuilder}
                    focusCohortSelectionRequestKey={focusCohortSelectionRequestKey}
                    />
                  ) : null}
                </>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box borderTop={`${drawerDividerWidth} solid ${drawerDividerColor}`} px={3} py={3} bgcolor="#FFFFFF">
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} flexWrap="wrap">
            <Box display="flex" gap={2} ml="auto">
              {areTenantFiltersEnabled ? (
                <Button 
                  variant="outlined" 
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleClearAll} 
                  sx={buttonOutlinedSx}
                  disabled={isClearAllDisabled}
                >
                  Clear All
                </Button>
              ) : null}
              <Button 
                variant="contained"
                onClick={handleApply}
                sx={buttonContainedSx}
                disabled={isReallyLoading || hasNoSelectedDivision || hasNoSelectedPlan}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default GlobalFiltersDrawer;
